import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle, X, ArrowLeft, Send, Search,
  Paperclip, Clock, Check, CheckCheck, AlertCircle, Image, FileText,
  Play, ChevronDown, Mic, Square, UserPlus, Users, GitMerge, CheckCircle, PauseCircle, Timer, Building2,
  Plus, Phone, PhoneCall, Loader2, ShoppingCart, Cpu, Video, MonitorUp, Zap, BookOpen, CalendarDays, Sparkles, BookUser,
  Mail, User, Link2, Briefcase, MapPin, Globe, CornerUpLeft, Pencil, KeyRound, Stethoscope
} from 'lucide-react';
import VoiceCallWidget from './VoiceCallWidget';
import CompanyLogo from '../CompanyLogo';
import FeatureSpotlight, { useFeatureSpotlight } from '../FeatureSpotlight';
import MergeTicketsModal from '../MergeTicketsModal';
import AiAssistPanel from '../AiAssistPanel';
import AiSuggestionCard from '../AiSuggestionCard';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import EmojiPickerButton from '../EmojiPickerButton';
import QuickQuoteModal from '../QuickQuoteModal';
import KBArticlePicker from '../kb/KBArticlePicker';
import ShareCredentialModal from '../vault/ShareCredentialModal';
import ImageLightbox from '../ImageLightbox';
const ScheduleVisitModal = React.lazy(() => import('../calendar/ScheduleVisitModal'));
import api from '../../utils/api';
import { track } from '../../utils/trackAction';
import { markDirty, clearDirty } from '../../utils/unsavedWork';
import { waFailReason } from '../../utils/waFailReason';
import { toSnippet } from '../../utils/plainText';
import { presenceStatus, presenceColor } from '../../utils/presence';
import useDraggable from '../../hooks/useDraggable';
import { bubblePanelStyle } from '../../utils/bubblePanel';
import { playFor } from '../../utils/notificationSounds';
import TicketQualityPreview from '../TicketQualityPreview';
import ClientContextPanel from '../ClientContextPanel';

// Una conversación del bubble ya no es necesariamente de WhatsApp: también llegan las
// de las plataformas integradas (Oxígeno y las que vengan). Todo lo propio de Meta —la
// ventana de 24 h, las plantillas— no aplica ahí, y mostrarlo sería decirle al agente
// que mande una plantilla para responderle a alguien que tiene el chat abierto.
// Sin `channel` se asume WhatsApp: así las conversaciones viejas siguen igual.
// `external_source` es lo que marca que la conversación viene de una plataforma
// integrada: esos tickets se crean con channel='portal', así que mirar `channel` daría
// un falso 'es WhatsApp' y le mostraría al agente avisos de plantilla que no aplican.
const esWhatsApp = (conv) => !conv || (!conv.external_source && (!conv.channel || conv.channel === 'whatsapp'));

// De qué plataforma viene la conversación. El agente TIENE que verlo: alguien que
// escribe desde Oxígeno está logueado con su cuenta corporativa, o sea que es personal
// de la Fundación — no es lo mismo que un WhatsApp de un número suelto, y cambia cómo
// se le responde. El color se fija por sistema para que se reconozca de un vistazo.
const ORIGEN = {
  oxigeno: { label: 'Oxígeno', color: '#23B5A6' },
  medisoft: { label: 'medisoft', color: '#7c3aed' },
};
// En medisoft cada consultorio es un chat distinto: la pastilla dice CUÁL ("Flom",
// "AW") y no sólo el sistema, que es lo que el agente necesita saber de un vistazo.
const consultorioCorto = (nombre) => {
  const n = String(nombre || '');
  if (/flom/i.test(n)) return 'Flom';
  if (/wainsztein/i.test(n)) return 'AW';
  return n.replace(/^(Dra?\.?\s+)/i, '').trim() || 'medisoft';
};
const origenDe = (conv) => {
  if (!conv || !conv.external_source) return null;
  if (conv.external_source === 'medisoft') return { ...ORIGEN.medisoft, label: consultorioCorto(conv.company_name) };
  return ORIGEN[conv.external_source] || { label: conv.external_source, color: '#6b7280' };
};

function PildoraOrigen({ conv, style }) {
  const o = origenDe(conv);
  if (!o) return null;
  return (
    <span
      title={`Chat embebido en ${o.label}`}
      style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase',
        padding: '1px 6px', borderRadius: 10, whiteSpace: 'nowrap',
        border: `1px solid ${o.color}`, color: o.color, background: `${o.color}18`,
        ...style,
      }}
    >{o.label}</span>
  );
}

/* El chirp de dos tonos vive ahora en utils/notificationSounds como 'wa-chirp', que
   sigue siendo el default de este aviso. Se mueve para que el usuario pueda cambiarlo
   —y para poder elegirlo en los otros avisos— sin duplicar el oscilador. */

/* ── Time helpers ── */
function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) {
    return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  }
  if (diff < 172800000) return 'Ayer';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
}

function formatSessionCountdown(expiresAt) {
  if (!expiresAt) return { text: 'Esperando respuesta del contacto', color: 'amber', expired: false, waiting: true };
  const remaining = new Date(expiresAt) - new Date();
  if (remaining <= 0) return { text: 'Expirada', color: 'red', expired: true };
  const hours = Math.floor(remaining / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  if (hours > 12) return { text: `${hours}h`, color: 'green', expired: false };
  if (hours > 2) return { text: `${hours}h ${mins}m`, color: 'green', expired: false };
  return { text: `${hours}h ${mins}m`, color: 'amber', expired: false };
}

// "hace 1h 12m" / "ayer 15:14" / "14-may 09:34" — used in the compose banner
// to tell the agent where the open 24h window comes from when it's a
// different ticket than the one displayed.
function formatWindowSourceWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 3600000) {
    const m = Math.max(1, Math.floor(diff / 60000));
    return `hace ${m}m`;
  }
  if (diff < 12 * 3600000) {
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `hace ${h}h ${m}m`;
  }
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const hhmm = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  if (sameDay) return `hoy ${hhmm}`;
  if (isYesterday) return `ayer ${hhmm}`;
  return `${d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })} ${hhmm}`;
}

function formatDateSeparator(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Hoy';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

/* ── Delivery tick icon ── */
function DeliveryTick({ status, reason }) {
  switch (status) {
    case 'sent': return <Check size={14} style={{ color: '#8696a0' }} />;
    case 'delivered': return <CheckCheck size={14} style={{ color: '#8696a0' }} />;
    case 'read': return <CheckCheck size={14} style={{ color: '#53bdeb' }} />;
    case 'failed':
      return (
        <span title={reason || 'No se pudo entregar'} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#ef4444', fontSize: 11, fontWeight: 600, cursor: 'help' }}>
          <AlertCircle size={13} /> No entregado
        </span>
      );
    default: return <Clock size={12} style={{ color: '#8696a0' }} />;
  }
}

/* ── Session dot ── */
function SessionDot({ expiresAt }) {
  const { color } = formatSessionCountdown(expiresAt);
  const c = color === 'green' ? '#22c55e' : color === 'amber' ? '#f59e0b' : '#ef4444';
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: c, marginRight: 6, flexShrink: 0 }} />;
}

/* Un mail que de verdad salió desde el ticket (no una respuesta de WhatsApp en tránsito). */
const isSentEmail = (msg) => msg.channel === 'email' && !!msg.email_message_id;

export default function WhatsAppBubble() {
  const { t } = useTranslation();
  const { user, hasModule } = useAuth();
  const navigate = useNavigate();

  const { pos: dragPos, isDragging, wasDragged, handlers: dragHandlers, elementRef: fabRef } = useDraggable('wa-bubble-pos', { defaultZone: 'bottom-right', width: 52, height: 52 });

  const [open, setOpen] = useState(false);
  // Instrumentación de la burbuja: cuándo se abrió y si la visita terminó en trabajo.
  // Abrir y cerrar sin tocar nada es una señal tan válida como enviar un mensaje —
  // y es la que no aparece en ningún lado si sólo se cuentan los envíos.
  const openedAtRef = useRef(null);
  const usedRef = useRef(false);
  const [conversations, setConversations] = useState([]);
  // "Escribirle a un consultorio" (chat medisoft): consultorios con chat activo.
  const [clinics, setClinics] = useState([]);
  const [showClinics, setShowClinics] = useState(false);
  const clinicSpot = useFeatureSpotlight('whatsapp_write_to_clinic');
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [msgText, setMsgText] = useState('');
  // "El cliente está escribiendo" en los chats embebidos. Se apaga solo a los 5 s: si el
  // cliente cierra la pestaña, el aviso de apagado nunca llega y el cartel quedaría fijo.
  const [clienteEscribiendo, setClienteEscribiendo] = useState(false);
  // "¿Esta persona ya la tenemos?" — sólo aparece en conversaciones que vienen de una
  // plataforma integrada, que es donde puede haberse creado un contacto nuevo para
  // alguien que ya nos escribía por WhatsApp.
  const [candidatos, setCandidatos] = useState(null);
  const [vinculando, setVinculando] = useState(false);
  useEffect(() => {
    if (!clienteEscribiendo) return;
    const id = setTimeout(() => setClienteEscribiendo(false), 5000);
    return () => clearTimeout(id);
  }, [clienteEscribiendo]);
  // Avisar al cliente que estamos escribiendo, como mucho una vez cada 3 s.
  const ultimoTypingRef = useRef(0);
  const avisarQueEscribo = useCallback((ticketId) => {
    if (!ticketId) return;
    const ahora = Date.now();
    if (ahora - ultimoTypingRef.current < 3000) return;
    ultimoTypingRef.current = ahora;
    api.post(`/tickets/${ticketId}/typing`, { active: true }).catch(() => {});
  }, []);
  // replyingTo: when set, the next sent message quotes this one (Meta context.message_id).
  // Holds the full message object so the composer can render a preview without re-fetching.
  const [replyingTo, setReplyingTo] = useState(null);
  // reactionPickerFor: msg id whose reaction picker is currently open
  const [reactionPickerFor, setReactionPickerFor] = useState(null);
  const [sending, setSending] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [search, setSearch] = useState('');
  const [totalUnread, setTotalUnread] = useState(0);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showCollabPicker, setShowCollabPicker] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [confirmResolve, setConfirmResolve] = useState(null); // null | 'resolved' | 'on_hold' | 'pending'
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolveSilent, setResolveSilent] = useState(false); // shift-click → don't notify client
  const [resolveSubject, setResolveSubject] = useState('');
  const [resolveDiagnosis, setResolveDiagnosis] = useState('');
  const [resolveSolution, setResolveSolution] = useState('');
  const [resolveOverride, setResolveOverride] = useState('');
  const [resolveSimple, setResolveSimple] = useState(false); // simple task → relaxed quality floors
  const [resolveSubmitting, setResolveSubmitting] = useState(false);
  const [aiProposingFields, setAiProposingFields] = useState(false);
  const [editingSubject, setEditingSubject] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState('');
  const [subjectSaving, setSubjectSaving] = useState(false);
  const [shiftHeld, setShiftHeld] = useState(false);
  useEffect(() => {
    const down = (e) => { if (e.key === 'Shift') setShiftHeld(true); };
    const up = (e) => { if (e.key === 'Shift') setShiftHeld(false); };
    const clear = () => setShiftHeld(false);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', clear);
    };
  }, []);
  const [showQuickQuote, setShowQuickQuote] = useState(false);
  const [showKBShare, setShowKBShare] = useState(false);
  const [showShareCred, setShowShareCred] = useState(false);
  const [showScheduleVisit, setShowScheduleVisit] = useState(false);
  const [showAiAssist, setShowAiAssist] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const quickActionsRef = useRef(null);
  useEffect(() => {
    if (!showQuickActions) return;
    const handle = (e) => { if (quickActionsRef.current && !quickActionsRef.current.contains(e.target)) setShowQuickActions(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showQuickActions]);
  const [agents, setAgents] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [voiceCallOpen, setVoiceCallOpen] = useState(false);
  // 'audio' | 'video'. El video sólo se ofrece en los chats embebidos: en WhatsApp el
  // link de la llamada viaja por Meta y no tiene sentido pedir cámara de ese lado.
  const [voiceCallMode, setVoiceCallMode] = useState('audio');
  // Llamada que abrió el CLIENTE desde el chat embebido (un clic de su lado) y en la que el
  // agente entra desde el aviso o desde el ticket. { id, mode, ticket_id, contact_name }
  const [joinCall, setJoinCall] = useState(null);
  const avisarLlamadaRef = useRef(null);
  avisarLlamadaRef.current = async (ticketId) => {
    try {
      // El aviso por socket llega a todos los internos: acá el servidor aplica el alcance
      // por empresa y el módulo de llamadas. Si no corresponde, no hay aviso.
      const { data } = await api.get('/voice-calls/waiting', { params: { ticket_id: ticketId } });
      const c = data?.call;
      if (!c) return;
      const nombre = c.contact_name || t('voiceCall.theClient');
      const texto = c.mode === 'screen' ? t('voiceCall.clientWaitingScreen', { name: nombre })
        : c.mode === 'video' ? t('voiceCall.clientWaitingVideo', { name: nombre })
        : t('voiceCall.clientWaiting', { name: nombre });
      playFor('ticketAttention');
      toast((tt) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13 }}>{texto}{c.ticket_number ? ` · #${c.ticket_number}` : ''}</span>
          <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}
                  onClick={() => { toast.dismiss(tt.id); setJoinCall(c); }}>
            <Phone size={14} /> {t('voiceCall.join')}
          </button>
        </span>
      ), { id: `voice-call-${c.id}`, duration: 60000 });
    } catch { /* sin permiso o sin módulo: no hay aviso */ }
  };
  // "Unirse" desde la vista del ticket: un solo lugar monta el widget.
  useEffect(() => {
    const unirse = (e) => { if (e.detail?.id) setJoinCall(e.detail); };
    window.addEventListener('voice-call:join', unirse);
    return () => window.removeEventListener('voice-call:join', unirse);
  }, []);
  const voiceCallSpotlight = useFeatureSpotlight('whatsapp_voice_call');
  const clientCtxSpotlight = useFeatureSpotlight('whatsapp_client_context_v1');
  const [companyList, setCompanyList] = useState(null); // lazy-loaded when needed
  const [clientContextOpen, setClientContextOpen] = useState(false); // ficha del cliente
  const [assigningCompany, setAssigningCompany] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // { query, startPos }
  const [mentionIndex, setMentionIndex] = useState(0);

  // New conversation state
  const [showNewConv, setShowNewConv] = useState(false);
  const [newConvSearch, setNewConvSearch] = useState('');
  const [newConvResults, setNewConvResults] = useState([]);
  const [newConvContact, setNewConvContact] = useState(null);
  const [newConvPhone, setNewConvPhone] = useState('');
  const [newConvName, setNewConvName] = useState('');
  const [newConvManual, setNewConvManual] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);
  const [newConvSending, setNewConvSending] = useState(false);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  // Unified composer state: window-aware, ticket-action-aware
  const [waState, setWaState] = useState(null); // { windowOpen, windowExpiresAt, openTicket, lastTicket, canAutoReopen }
  const [waStateLoading, setWaStateLoading] = useState(false);
  const [composeBody, setComposeBody] = useState('');
  const [ticketAction, setTicketAction] = useState('auto'); // 'auto' | 'reopen' | 'new'
  const [forceTemplate, setForceTemplate] = useState(false); // agent can opt into a template even when window open
  const usableTemplates = useMemo(() => templates.filter(t => !t.template_name.startsWith('hello_world')), [templates]);

  // Load templates eagerly so they're available for expired session re-sends
  useEffect(() => {
    if (!templatesLoaded) {
      api.get('/whatsapp/templates/approved')
        .then(({ data }) => { setTemplates(Array.isArray(data) ? data : []); setTemplatesLoaded(true); })
        .catch(() => {});
    }
  }, [templatesLoaded]);
  const searchTimerRef = useRef(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const pollRef = useRef(null);
  const selectedConvRef = useRef(null);
  const prevConvIdsRef = useRef(new Set());
  const firstPollRef = useRef(true);
  // Serializa fetchConversations: el socket (ticket:updated) y el poll de 10s pueden
  // pedir la lista al mismo tiempo, y las dos respuestas comparaban contra el MISMO
  // prevConvIds (se actualiza recién al volver) → el mismo mensaje sonaba y toasteaba
  // dos veces. Santi, 17-sep-2026: "se disparan varios mensajes de notificación".
  const fetchInFlightRef = useRef(false);
  const fetchPendingRef = useRef(false);
  const fetchSelfRef = useRef(null); // apunta a fetchConversations (para re-correrla desde su propio finally)
  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Keep ref in sync for socket handler
  useEffect(() => { selectedConvRef.current = selectedConv; }, [selectedConv]);

  // Load agents for @mention autocomplete
  useEffect(() => {
    api.get('/agents').then(r => {
      const list = (r.data.agents || r.data).filter(a => a.is_active);
      setAgents(list);
    }).catch(() => {});
  }, []);

  // @mention matches
  const mentionMatches = mentionQuery
    ? agents.filter(a => {
        const q = mentionQuery.query.toLowerCase();
        return a.first_name?.toLowerCase().startsWith(q) ||
          `${a.first_name} ${a.last_name}`.toLowerCase().includes(q);
      }).filter(a => a.id !== user.id).slice(0, 5)
    : [];

  // ── Load messages for selected conversation ──
  const loadMessages = useCallback(async (ticketId) => {
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      // Stash diagnosis/solution + completeness onto selectedConv so the resolve modal
      // can prefill and render the live quality preview without an extra fetch.
      if (data.ticket) {
        setSelectedConv(prev => prev && prev.id === ticketId
          ? { ...prev, diagnosis: data.ticket.diagnosis, solution: data.ticket.solution, kind: data.ticket.kind, completeness: data.completeness }
          : prev);
      }
    } catch (err) {
      console.error('[WA-BUBBLE] Load messages error:', err);
    }
    setLoadingMessages(false);
  }, []);
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);

  // ── Fetch conversations ──
  const fetchConversations = useCallback(async (fromSocket = false) => {
    if (fetchInFlightRef.current) { fetchPendingRef.current = true; return; }
    fetchInFlightRef.current = true;
    try {
      const { data } = await api.get('/tickets/wa-conversations');
      const list = Array.isArray(data) ? data : [];
      setConversations(list);
      const unread = list.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setTotalUnread(unread);

      // Detect new contact messages — play WA sound + toast (skip first poll)
      if (!firstPollRef.current) {
        const prevIds = prevConvIdsRef.current;
        for (const c of list) {
          const key = `${c.id}-${c.last_message_at}`;
          if (!prevIds.has(key) && c.last_message_author === 'contact' && c.unread_count > 0) {
            playFor('whatsapp');
            // De una plataforma integrada, el aviso dice de dónde viene ("AW · Nombre") y
            // toma su color: un verde de WhatsApp haría creer que escribió por WhatsApp.
            const origen = origenDe(c);
            const name = c.contact_name || c.contact_phone || 'WhatsApp';
            const preview = c.last_message_text ? c.last_message_text.substring(0, 50) : t('whatsapp.bubble.newMessage', 'Nuevo mensaje');
            toast(
              `${origen ? `${origen.label} · ` : ''}${name}: ${preview}`,
              { icon: '💬', duration: 6000, style: { background: origen?.color || '#25d366', color: '#fff', fontWeight: 600, fontSize: 13 } }
            );
            break; // one sound per batch
          }
        }
      }
      prevConvIdsRef.current = new Set(list.map(c => `${c.id}-${c.last_message_at}`));
      firstPollRef.current = false;

      // Sync selectedConv with fresh data (contact_name, company_name, etc.)
      const current = selectedConvRef.current;
      if (current) {
        const fresh = list.find(c => c.id === current.id);
        if (fresh) {
          setSelectedConv(prev => ({
            ...prev,
            contact_name: fresh.contact_name,
            contact_phone: fresh.contact_phone,
            company_name: fresh.company_name,
            company_id: fresh.company_id,
            company_suspended: fresh.company_suspended,
            label_color: fresh.label_color,
            status: fresh.status,
            assigned_to: fresh.assigned_to,
            agent_first_name: fresh.agent_first_name,
            agent_last_name: fresh.agent_last_name,
            unread_count: fresh.unread_count,
            wa_session_expires_at: fresh.wa_session_expires_at,
          }));
        }
      }
    } catch (err) {
      console.error('[WA-BUBBLE] Fetch conversations error:', err);
    } finally {
      fetchInFlightRef.current = false;
      // Si llegó otro pedido mientras éste estaba en vuelo, se corre UNA vez más
      // (no una por cada evento) ya con prevConvIds al día.
      if (fetchPendingRef.current) {
        fetchPendingRef.current = false;
        fetchSelfRef.current?.(fromSocket);
      }
    }
  }, []);
  useEffect(() => { fetchSelfRef.current = fetchConversations; }, [fetchConversations]);

  // ── Poll conversations (only when bubble is expanded) ──
  useEffect(() => {
    if (!user) return;
    // Always fetch once on mount for unread badge, but only poll when open
    fetchConversations();
    if (!open) return;
    pollRef.current = setInterval(fetchConversations, 10000);
    return () => clearInterval(pollRef.current);
  }, [user, fetchConversations, open]);

  // ── Socket.io (root namespace for ticket:updated + whatsapp:status) ──
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const API_BASE = (process.env.REACT_APP_API_URL || '/api').replace('/api', '');
    const s = io(API_BASE, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      // Root namespace now requires auth; function form re-reads the token on each
      // reconnect so a refreshed JWT is picked up automatically.
      auth: (cb) => cb({ token: localStorage.getItem('accessToken') }),
    });

    s.on('connect', () => {
      console.log('[WA-BUBBLE] Socket connected');
    });

    s.on('ticket:updated', (data) => {
      // No sólo WhatsApp: los chats de las plataformas integradas llegan con SU canal
      // ('oxigeno' y los que vengan). Con el guard viejo, un mensaje del cliente entraba
      // a la base y el bubble no se enteraba nunca.
      if (data.channel) {
        fetchConversations(true);
        const current = selectedConvRef.current;
        if (current && data.ticketId === current.id) {
          loadMessagesRef.current(current.id);
        }
      }
    });

    s.on('voice-call:client-waiting', (data) => {
      if (!data?.ticketId) return;
      avisarLlamadaRef.current?.(data.ticketId);
      // La vista del ticket no tiene socket propio: se entera por acá.
      window.dispatchEvent(new CustomEvent('voice-call:waiting', { detail: data }));
    });

    s.on('integration:typing', (data) => {
      const current = selectedConvRef.current;
      if (!current || data.ticketId !== current.id || data.from !== 'client') return;
      setClienteEscribiendo(!!data.active);
    });

    s.on('whatsapp:status', (data) => {
      setMessages(prev => prev.map(m =>
        m.wa_message_id === data.messageId ? { ...m, wa_status: data.status, wa_error: data.error ?? m.wa_error } : m
      ));
    });

    // Reactions arrive in real time — patch the matching message in place so the badge
    // updates without reloading the whole conversation.
    s.on('whatsapp:reaction', (data) => {
      window.dispatchEvent(new CustomEvent('ticket:wa-reaction', { detail: data }));
      const current = selectedConvRef.current;
      if (!current || data.ticketId !== current.id) return;
      setMessages(prev => prev.map(m =>
        m.id === data.messageId ? { ...m, wa_reactions: data.wa_reactions } : m
      ));
    });

    socketRef.current = s;
    return () => { s.disconnect(); socketRef.current = null; };
  }, [user, fetchConversations]); // loadMessages accessed via ref

  // ── Select conversation ──
  const selectConversation = useCallback((conv) => {
    usedRef.current = true;
    track('whatsapp', 'bubble_conv_open', conv?.status || null, { result: 'ok' });
    setSelectedConv(conv);
    // Recuperar borrador sin enviar de este chat (sobrevive reload/deploy).
    let restored = '';
    try {
      const saved = localStorage.getItem(`wa-draft-${conv.id}`);
      if (saved && saved.trim()) restored = saved;
    } catch {}
    setMsgText(restored);
    setReplyingTo(null);
    setReactionPickerFor(null);
    setMentionQuery(null);
    setShowCollabPicker(false);
    setCollaborators([]);
    loadMessages(conv.id);
    // Chat de un consultorio de medisoft: abrirlo marca como leído lo que mandaron.
    if (conv?.external_source === 'medisoft') api.post(`/medisoft-chat/${conv.id}/read`).catch(() => {});
    // Fetch collaborators
    api.get(`/tickets/${conv.id}/collaborators`).then(r => setCollaborators(r.data)).catch(() => {});
    // Auto-focus textarea
    setTimeout(() => textareaRef.current?.focus(), 100);
  }, [loadMessages]);

  // ── Auto-guardar borrador del chat activo ──
  // Persiste el texto sin enviar por conversación (se recupera tras reload/deploy) y
  // marca "trabajo sin guardar" para que el auto-update no recargue en el medio.
  useEffect(() => {
    const conv = selectedConv;
    if (!conv?.id) return;
    const key = `wa-draft-${conv.id}`;
    const timer = setTimeout(() => {
      try {
        if (msgText && msgText.trim()) {
          localStorage.setItem(key, msgText);
          markDirty(key);
        } else {
          localStorage.removeItem(key);
          clearDirty(key);
        }
      } catch {}
    }, 800);
    return () => clearTimeout(timer);
  }, [msgText, selectedConv]);

  // Otros tickets de WhatsApp del MISMO contacto, que es el caso real de duplicado:
  // el cliente escribe de nuevo y se abre un ticket aparte por el mismo tema. La lista
  // de conversaciones ya está en memoria, así que no hace falta pedir nada al servidor.
  // Pedido de Santi (vía Thiago, 31-ago-2026): darse cuenta de que son dos tickets del
  // mismo tema pasa MIENTRAS leés el chat, y hasta ahora había que irse al listado.
  const dupCandidates = React.useMemo(() => {
    if (!selectedConv?.contact_phone) return [];
    return conversations.filter(c => c.id !== selectedConv.id && c.contact_phone === selectedConv.contact_phone);
  }, [conversations, selectedConv]);

  // ── Collaborator management ──
  const openCollabPicker = async () => {
    const opening = !showCollabPicker;
    setShowCollabPicker(opening);
    if (opening) {
      try {
        const { data } = await api.get('/agents');
        const list = data.agents || data;
        setAgents(list.filter(a => a.is_active));
      } catch (e) { console.error('[WA-BUBBLE] Agents fetch error:', e); }
    }
  };

  const addCollaborator = async (agentId) => {
    if (!selectedConv) return;
    try {
      await api.post(`/tickets/${selectedConv.id}/collaborators`, { userId: agentId });
      const { data } = await api.get(`/tickets/${selectedConv.id}/collaborators`);
      setCollaborators(data);
      toast.success(t('common.saved'));
    } catch (e) { console.error('[WA-BUBBLE] Add collaborator error:', e); toast.error(t('common.actionFailed')); }
  };

  const removeCollaborator = async (agentId) => {
    if (!selectedConv) return;
    try {
      await api.delete(`/tickets/${selectedConv.id}/collaborators/${agentId}`);
      setCollaborators(prev => prev.filter(c => c.user_id !== agentId));
      toast.success(t('common.deleted'));
    } catch (e) { console.error('[WA-BUBBLE] Remove collaborator error:', e); toast.error(t('common.actionFailed')); }
  };

  // ── Resolve / Close ticket ──
  // For status='resolved', the backend enforces a quality gate (diagnosis +
  // solution must be present). We collect those in the modal and PATCH them
  // alongside status. Same pattern the ticket detail page uses (see commit
  // 8482f9f); without it the bubble's Resolved button silently 400'd.
  const MIN_SUBJECT = 10;
  const MIN_DIAG = 30;
  const MIN_SOL = 80;
  const handleResolveTicket = async () => {
    if (!selectedConv || !confirmResolve || resolveSubmitting) return;
    const isResolve = confirmResolve === 'resolved';
    // Notification/validation tickets are exempt from the gate (matches backend logic).
    const exempt = selectedConv.kind === 'notification' || selectedConv.status === 'validation';
    const simple = resolveSimple;
    const minSubj = simple ? 1 : MIN_SUBJECT;
    const minDiag = simple ? 1 : MIN_DIAG;
    const minSol = simple ? 1 : MIN_SOL;
    const subj = resolveSubject.trim();
    const diag = resolveDiagnosis.trim();
    const sol = resolveSolution.trim();
    const override = resolveOverride.trim();
    const isAdmin = user?.role === 'admin';
    const passes = subj.length >= minSubj && diag.length >= minDiag && sol.length >= minSol;
    if (isResolve && !exempt && !passes && !(isAdmin && override)) {
      const missing = [];
      if (subj.length < minSubj) missing.push(t('tickets.completeness.subject'));
      if (diag.length < minDiag) missing.push(t('tickets.completeness.diagnosis'));
      if (sol.length < minSol) missing.push(t('tickets.completeness.solution'));
      toast.error(t('tickets.qualityRequiredFields', { fields: missing.join(', ') }));
      return;
    }
    setResolveSubmitting(true);
    try {
      const payload = { status: confirmResolve };
      if (isResolve) {
        if (subj && subj !== (selectedConv.subject || '')) payload.subject = subj;
        if (diag) payload.diagnosis = diag;
        if (sol) payload.solution = sol;
        const notes = resolveNotes.trim();
        if (notes) payload.resolutionNotes = notes;
        if (!passes && isAdmin && override) payload.qualityOverrideReason = override;
        if (simple) payload.simpleResolve = true;
      }
      if (resolveSilent) payload.skipClientNotification = true;
      await api.patch(`/tickets/${selectedConv.id}`, payload);
      setConfirmResolve(null);
      setResolveNotes('');
      setResolveSilent(false);
      setResolveSubject('');
      setResolveDiagnosis('');
      setResolveSolution('');
      setResolveOverride('');
      setResolveSimple(false);
      if (isResolve) {
        toast.success(t('tickets.resolvedSuccess', 'Ticket resuelto'));
        setSelectedConv(null);
        setMessages([]);
      } else {
        setSelectedConv(prev => prev ? { ...prev, status: confirmResolve } : prev);
      }
      fetchConversations();
    } catch (e) {
      console.error('[WA-BUBBLE] Resolve error:', e);
      if (e?.response?.status === 400 && e.response.data?.error === 'ticket_quality_required') {
        const missing = Array.isArray(e.response.data.missing) ? e.response.data.missing : [];
        if (missing.length) {
          const fields = missing.map(m => t(`tickets.completeness.${m}`)).join(', ');
          toast.error(t('tickets.qualityRequiredFields', { fields }));
        } else {
          toast.error(t('tickets.qualityRequired'));
        }
      } else {
        toast.error(t('common.error', 'Error'));
      }
    } finally {
      setResolveSubmitting(false);
    }
  };

  // ── Quick resolve (no diagnosis/solution required) ──
  // Mirrors the ⚡ shortcut on TicketDetailPage / TicketsPage so agents can close
  // throwaway WA threads (one-line "ok gracias") without filling the quality form.
  // Stamped with quick_resolved_at so it's excluded from the quality ranking.
  const handleQuickResolve = async () => {
    if (!selectedConv || resolveSubmitting) return;
    if (!window.confirm(t('tickets.quickResolveConfirm'))) return;
    setResolveSubmitting(true);
    try {
      await api.patch(`/tickets/${selectedConv.id}`, {
        status: 'resolved',
        quickResolve: true,
        skipNotificationEmail: true,
        skipClientNotification: true,
      });
      toast.success(t('tickets.quickResolved'));
      setSelectedConv(null);
      setMessages([]);
      fetchConversations();
    } catch (e) {
      console.error('[WA-BUBBLE] Quick resolve error:', e);
      toast.error(t('common.error', 'Error'));
    } finally {
      setResolveSubmitting(false);
    }
  };

  // ── Close reaction picker on outside click (Esc also works) ──
  useEffect(() => {
    if (!reactionPickerFor) return;
    const onDocClick = () => setReactionPickerFor(null);
    const onKey = (e) => { if (e.key === 'Escape') setReactionPickerFor(null); };
    // Defer to avoid catching the click that opened it
    const t = setTimeout(() => {
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [reactionPickerFor]);

  // ── AI propose fields (mirrors TicketDetailPage resolve flow) ──
  // Only fills empty fields — never overwrites the agent's typing.
  const proposeFieldsFromAI = useCallback(async () => {
    if (!selectedConv?.id) return;
    setAiProposingFields(true);
    try {
      const { data } = await api.post(`/tickets/${selectedConv.id}/ai-propose-fields`);
      setResolveDiagnosis(prev => prev.trim() ? prev : (data.proposal?.diagnosis || prev));
      setResolveSolution(prev => prev.trim() ? prev : (data.proposal?.solution || prev));
      toast.success(t('reviewQueue.proposalGenerated'));
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'daily_cap_reached') toast.error(t('reviewQueue.aiCapReached'));
      else if (code === 'no_messages' || code === 'thread_too_short') toast.error(t('reviewQueue.threadTooShort'));
      else toast.error(t('reviewQueue.aiError'));
    } finally {
      setAiProposingFields(false);
    }
  }, [selectedConv?.id, t]);

  // ── Save subject (ticket title) edit from header ──
  const saveSubject = useCallback(async () => {
    if (!selectedConv?.id || subjectSaving) return;
    const next = subjectDraft.trim();
    if (!next || next === selectedConv.subject) {
      setEditingSubject(false);
      return;
    }
    setSubjectSaving(true);
    try {
      await api.patch(`/tickets/${selectedConv.id}`, { subject: next });
      setSelectedConv(prev => prev ? { ...prev, subject: next } : prev);
      setConversations(prev => prev.map(c => c.id === selectedConv.id ? { ...c, subject: next } : c));
      setEditingSubject(false);
      toast.success(t('common.saved', 'Guardado'));
    } catch (err) {
      console.error('[WA-BUBBLE] Save subject error:', err);
      toast.error(t('common.error', 'Error'));
    } finally {
      setSubjectSaving(false);
    }
  }, [selectedConv, subjectDraft, subjectSaving, t]);

  // ── Auto-scroll on new messages ──
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // ── Send message ──
  const buscarCandidatos = async () => {
    if (!selectedConv?.contact_id) return;
    if (candidatos) { setCandidatos(null); return; }   // segundo click: cerrar
    try {
      const { data } = await api.get(`/contacts/${selectedConv.contact_id}/link-candidates`);
      setCandidatos(data.candidates || []);
    } catch { setCandidatos([]); }
  };

  const vincularCon = async (cand) => {
    if (!selectedConv?.contact_id || vinculando) return;
    const ok = window.confirm(t('whatsapp.link.confirm', {
      a: cand.name, b: selectedConv.contact_name || '',
      defaultValue: `¿Unificar este contacto con "${cand.name}"? Queda una sola ficha con todo el historial.`,
    }));
    if (!ok) return;
    setVinculando(true);
    try {
      // El candidato es el PRIMARIO: es el que ya tiene historial y equipos. El contacto
      // recién creado por la plataforma es el que se absorbe.
      await api.post('/contacts/merge', { primaryId: cand.id, secondaryId: selectedConv.contact_id });
      toast.success(t('whatsapp.link.done', 'Contactos unificados'));
      setCandidatos(null);
      await fetchConversations();
    } catch {
      toast.error(t('common.actionFailed', 'No se pudo completar la acción'));
    } finally { setVinculando(false); }
  };

  // Aceptar la pantalla compartida. Crear la sesión ES la aceptación: el endpoint avisa
  // a la sala del chat y al cliente le aparece el botón para compartir en el acto. Sirve
  // igual para el caso inverso —arrancarla nosotros— porque el cliente recibe lo mismo.
  const [abriendoAsistencia, setAbriendoAsistencia] = useState(false);
  const abrirAsistencia = async () => {
    if (!selectedConv || abriendoAsistencia) return;
    setAbriendoAsistencia(true);
    // La pestaña se abre YA, dentro del clic. Si se abre después del await, ya no cuenta
    // como gesto del usuario y el navegador la puede bloquear como popup sin avisar.
    const pestaña = window.open('about:blank', '_blank');
    try {
      const { data } = await api.post('/remote-assist/sessions', {
        ticket_id: selectedConv.id,
        company_id: selectedConv.company_id || null,
        contact_name: selectedConv.contact_name || '',
        notes: `Abierta desde el chat de ${selectedConv.external_source || 'soporte'}`,
      });
      // El AGENTE va al visor (/remote-assist/:id). /assist/:token es la página del
      // CLIENTE —la que pide compartir la pantalla— y es la que recibe él por socket.
      // Antes el agente abría /assist/:token: su navegador se ponía a compartir SU
      // escritorio, el visor nunca se abría y la sesión moría sin que el cliente conecte.
      const destino = data?.id ? `/remote-assist/${data.id}` : null;
      if (pestaña && destino) { pestaña.opener = null; pestaña.location.href = destino; }
      else if (destino) window.open(destino, '_blank', 'noopener');
      toast.success(t('whatsapp.assist.opened', 'Sesión de pantalla abierta — el cliente ya puede compartir'));
    } catch {
      if (pestaña) pestaña.close();
      toast.error(t('common.actionFailed', 'No se pudo completar la acción'));
    } finally { setAbriendoAsistencia(false); }
  };

  const handleSend = async () => {
    if (!msgText.trim() || !selectedConv || sending) return;
    setSending(true);
    try {
      const payload = { body: msgText.trim() };
      if (replyingTo?.id) payload.replyToMessageId = replyingTo.id;
      const { data } = await api.post(`/tickets/${selectedConv.id}/messages`, payload);
      usedRef.current = true;
      track('whatsapp', 'bubble_send', replyingTo?.id ? 'con_cita' : 'directo', { result: 'ok' });
      setMsgText('');
      try { localStorage.removeItem(`wa-draft-${selectedConv.id}`); } catch {}
      clearDirty(`wa-draft-${selectedConv.id}`);
      setReplyingTo(null);
      setMentionQuery(null);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      // Backend may redirect a message on a closed WA ticket to a new linked
      // child — follow the redirect so the bubble shows the live thread.
      if (data?.redirectedToTicket?.id) {
        const newId = data.redirectedToTicket.id;
        toast.success(t('whatsapp.compose.redirectedToNew', { n: data.redirectedToTicket.ticket_number, defaultValue: `Ticket cerrado — se creó #${data.redirectedToTicket.ticket_number} para esta conversación` }));
        await fetchConversations();
        selectConversation({ id: newId });
      } else {
        await loadMessages(selectedConv.id);
        fetchConversations();
      }
    } catch (err) {
      // Un envío registrado sólo cuando sale bien esconde su propia tasa de fallo,
      // que es justo lo que esta tabla existe para no perder.
      track('whatsapp', 'bubble_send', null, { result: 'fail' });
      console.error('[WA-BUBBLE] Send error:', err);
    }
    setSending(false);
    // Re-focus textarea so user can keep typing
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // ── Send attachment ──
  const handleAttachment = async (e) => {
    const files = e.target.files;
    if (!files?.length || !selectedConv) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('body', '');
      if (replyingTo?.id) formData.append('replyToMessageId', replyingTo.id);
      for (const file of files) {
        formData.append('attachments', file);
      }
      await api.post(`/tickets/${selectedConv.id}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReplyingTo(null);
      await loadMessages(selectedConv.id);
      fetchConversations();
    } catch (err) {
      console.error('[WA-BUBBLE] Attachment error:', err);
    }
    setSending(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // ── React to a WhatsApp message ──
  // Sends emoji to WhatsApp via Cloud API (or removes it when emoji is the same as current
  // user's existing reaction, matching WhatsApp Web's tap-again-to-remove behavior).
  const handleReact = async (msg, emoji) => {
    if (!selectedConv || !msg?.id) return;
    setReactionPickerFor(null);
    try {
      // Toggle: if my current reaction is the same emoji, send empty to remove.
      const myKey = user?.id;
      const mine = msg.wa_reactions?.[myKey]?.emoji;
      const finalEmoji = mine === emoji ? '' : emoji;
      await api.post(`/tickets/${selectedConv.id}/messages/${msg.id}/wa-reaction`, { emoji: finalEmoji });
      await loadMessages(selectedConv.id);
    } catch (err) {
      console.error('[WA-BUBBLE] React error:', err);
      toast.error(t('whatsapp.bubble.reactFailed', 'No se pudo reaccionar'));
    }
  };

  // ── Key handler ──
  const handleKeyDown = (e) => {
    // Handle mention autocomplete navigation
    if (mentionQuery && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex(prev => (prev + 1) % mentionMatches.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex(prev => (prev - 1 + mentionMatches.length) % mentionMatches.length); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); insertMention(mentionMatches[mentionIndex]); return; }
      if (e.key === 'Escape') { setMentionQuery(null); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Audio recording ──
  const startRecording = async () => {
    if (!selectedConv || recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(recordingTimerRef.current);
        setRecordingTime(0);

        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 500) return; // too short, discard

        setSending(true);
        try {
          const formData = new FormData();
          formData.append('body', '\uD83C\uDFA4 Nota de voz');
          if (replyingTo?.id) formData.append('replyToMessageId', replyingTo.id);
          formData.append('attachments', blob, `voice-${Date.now()}.webm`);
          await api.post(`/tickets/${selectedConv.id}/messages`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          setReplyingTo(null);
          await loadMessages(selectedConv.id);
          fetchConversations();
        } catch (err) {
          console.error('[WA-BUBBLE] Voice send error:', err);
        }
        setSending(false);
      };

      mediaRecorder.start(250);
      mediaRecorderRef.current = mediaRecorder;
      setRecording(true);
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('[WA-BUBBLE] Mic access error:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    setRecording(false);
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = () => {
        mediaRecorderRef.current?.stream?.getTracks().forEach(t => t.stop());
      };
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }
    clearInterval(recordingTimerRef.current);
    setRecording(false);
    setRecordingTime(0);
    audioChunksRef.current = [];
  };

  const formatRecTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ── Shared contact card (vCard) actions ──
  const [linkingSharedContact, setLinkingSharedContact] = useState({}); // { [key]: bool }

  const openComposeWithPhone = async (phone, name) => {
    // Leave any currently-open conversation so the new-conv panel can render.
    // The outer ternary in the bubble body is `!selectedConv ? newConv : chatView`,
    // so if a chat is open (e.g. user clicked the "WhatsApp" button on a vCard
    // shared inside a conversation, or fired wa-bubble:compose from a ticket
    // sidebar while a chat was open), showNewConv alone has no visible effect.
    setSelectedConv(null);
    setShowNewConv(true);
    setNewConvSearch('');
    setNewConvResults([]);
    setNewConvContact(null);
    setNewConvPhone(phone || '');
    setNewConvName(name || '');
    setNewConvManual(true);
    setSelectedTemplate(null);
    setTemplateParams([]);
    if (!templatesLoaded) {
      try {
        const { data } = await api.get('/whatsapp/templates/approved');
        setTemplates(Array.isArray(data) ? data : []);
        setTemplatesLoaded(true);
      } catch (err) { /* ignore */ }
    }
  };

  // ── External open requests (e.g. from ticket sidebar) ──
  useEffect(() => {
    const handleOpenTicket = (e) => {
      const ticketId = e.detail?.ticketId;
      if (!ticketId) return;
      setOpen(true);
      const match = conversations.find(c => c.id === ticketId);
      selectConversation(match || { id: ticketId });
    };
    const handleCompose = (e) => {
      const { phone, name } = e.detail || {};
      setOpen(true);
      openComposeWithPhone(phone, name);
    };
    window.addEventListener('wa-bubble:open-ticket', handleOpenTicket);
    window.addEventListener('wa-bubble:compose', handleCompose);
    return () => {
      window.removeEventListener('wa-bubble:open-ticket', handleOpenTicket);
      window.removeEventListener('wa-bubble:compose', handleCompose);
    };
  }, [conversations, selectConversation]); // openComposeWithPhone captured at invocation

  const handleLinkSharedContact = async (card, phoneObj, msgId) => {
    if (!selectedConv?.id) return;
    const key = `${msgId}:${phoneObj?.phone || phoneObj?.wa_id || ''}`;
    setLinkingSharedContact(prev => ({ ...prev, [key]: true }));
    try {
      const displayName = card.name?.formatted_name
        || [card.name?.first_name, card.name?.last_name].filter(Boolean).join(' ').trim()
        || phoneObj?.phone || phoneObj?.wa_id || t('whatsapp.vcard.unnamed', 'Contacto');
      const { data } = await api.post(`/tickets/${selectedConv.id}/shared-contact`, {
        name: displayName,
        phone: phoneObj?.phone || phoneObj?.wa_id || null,
        email: card.emails?.[0]?.email || null,
        org_company: card.org?.company || null,
        org_title: card.org?.title || null,
        source_message_id: msgId,
      });
      toast.success(data.created
        ? t('whatsapp.vcard.contactCreated', 'Contacto agregado y vinculado al ticket')
        : t('whatsapp.vcard.contactLinked', 'Contacto vinculado al ticket'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('whatsapp.vcard.linkFailed', 'Error al vincular contacto'));
    } finally {
      setLinkingSharedContact(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  // ── New conversation helpers ──
  // Consultorios de medisoft con chat activo. Se piden una vez: si no hay ninguno, el
  // botón no aparece y el bubble queda igual que siempre.
  useEffect(() => {
    api.get('/medisoft-chat/clinics').then(r => setClinics(r.data?.clinics || [])).catch(() => setClinics([]));
  }, []);

  const startClinicChat = async (clinic) => {
    setShowClinics(false);
    try {
      const { data } = await api.post('/medisoft-chat/start', { company_id: clinic.id });
      await fetchConversations(true);
      const { data: fresh } = await api.get(`/tickets/${data.ticket_id}`);
      selectConversation({ ...fresh, id: data.ticket_id });
      if (data.created) toast.success(t('whatsapp.bubble.clinicChatOpened', { name: clinic.name, defaultValue: `Chat abierto con ${clinic.name}` }));
    } catch {
      toast.error(t('common.actionFailed'));
    }
  };

  const openNewConv = async () => {
    setShowNewConv(true);
    setNewConvSearch('');
    setNewConvResults([]);
    setNewConvContact(null);
    setNewConvPhone('');
    setNewConvName('');
    setNewConvManual(false);
    setSelectedTemplate(null);
    setTemplateParams([]);
    // Fetch approved templates once
    if (!templatesLoaded) {
      try {
        const { data } = await api.get('/whatsapp/templates/approved');
        setTemplates(Array.isArray(data) ? data : []);
        setTemplatesLoaded(true);
      } catch (err) { console.error('[WA-BUBBLE] Templates fetch error:', err); }
    }
  };

  const closeNewConv = () => {
    setShowNewConv(false);
    setNewConvSearch('');
    setNewConvResults([]);
    setNewConvContact(null);
    setSelectedTemplate(null);
    setTemplateParams([]);
    setNewConvManual(false);
    setWaState(null);
    setComposeBody('');
    setTicketAction('auto');
    setForceTemplate(false);
  };

  // Fetch contact WA state (window + existing ticket) to drive the composer
  const fetchWaState = useCallback(async ({ contactId, phone } = {}) => {
    setWaStateLoading(true);
    try {
      const params = new URLSearchParams();
      if (contactId) params.set('contactId', contactId);
      else if (phone) params.set('phone', phone);
      const { data } = await api.get(`/tickets/wa-contact-state?${params.toString()}`);
      setWaState(data);
      // Smart default for the reopen/new toggle
      if (data.openTicket) setTicketAction('auto');
      else if (data.canAutoReopen) setTicketAction('reopen');
      else setTicketAction('new');
      setForceTemplate(false);
    } catch (err) {
      console.error('[WA-BUBBLE] waState fetch error:', err);
      setWaState(null);
    } finally {
      setWaStateLoading(false);
    }
  }, []);

  // Re-fetch state when target changes (contact picked / manual phone typed)
  useEffect(() => {
    if (newConvContact?.id) {
      fetchWaState({ contactId: newConvContact.id });
    } else if (newConvManual) {
      const digits = (newConvPhone || '').replace(/\D/g, '');
      if (digits.length >= 8) fetchWaState({ phone: newConvPhone });
      else setWaState(null);
    } else {
      setWaState(null);
    }
  }, [newConvContact, newConvManual, newConvPhone, fetchWaState]);

  const handleNewConvSearch = (val) => {
    setNewConvSearch(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (val.trim().length < 1) { setNewConvResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(val.trim())}`);
        setNewConvResults(data.results || []);
      } catch (err) { console.error('[WA-BUBBLE] Contact search error:', err); }
    }, 150);
  };

  const selectNewConvContact = (contact) => {
    setNewConvContact(contact);
    setNewConvPhone(contact.phone || '');
    setNewConvName(contact.name || '');
    setNewConvManual(false);
    // If template already selected, re-fill nombre param
    if (selectedTemplate && templateParamNames.length > 0) {
      const contactName = contact.name || '';
      setTemplateParams(prev => prev.map((val, idx) => {
        const pName = (templateParamNames[idx] || '').toLowerCase();
        return ['nombre', 'name', '1'].includes(pName) ? contactName : val;
      }));
      setNameAutoFilled(!!contactName);
    }
  };

  // Auto-select template when only one usable template exists
  useEffect(() => {
    if (usableTemplates.length === 1 && !selectedTemplate && (newConvContact || (newConvManual && newConvPhone.trim()))) {
      handleSelectTemplate(usableTemplates[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usableTemplates, newConvContact, newConvManual, newConvPhone]);

  // Parse template variable names — supports {{1}} (legacy) and {{nombre}} (named)
  const [templateParamNames, setTemplateParamNames] = useState([]);
  // True only when the nombre param was auto-filled from a real contact record.
  // Used to lock the field (so the contact's name isn't accidentally edited) WITHOUT
  // locking it the moment an agent types the first char into an empty/manual field.
  const [nameAutoFilled, setNameAutoFilled] = useState(false);

  const handleSelectTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    const body = tpl.body_text || '';
    // Match both {{1}} and {{variable_name}}
    const matches = [...body.matchAll(/\{\{(\w+)\}\}/g)];
    const seen = new Set();
    const names = [];
    for (const m of matches) {
      if (!seen.has(m[1])) { seen.add(m[1]); names.push(m[1]); }
    }
    setTemplateParamNames(names);
    // Auto-fill nombre from contact
    const contactName = newConvContact?.name || newConvName || '';
    const filled = names.map(n =>
      ['nombre', 'name', '1'].includes(n.toLowerCase()) ? contactName : ''
    );
    setTemplateParams(filled);
    setNameAutoFilled(!!contactName);
  };

  const updateTemplateParam = (idx, val) => {
    setTemplateParams(prev => { const n = [...prev]; n[idx] = val; return n; });
  };

  const getTemplatePreview = () => {
    if (!selectedTemplate) return '';
    let text = selectedTemplate.body_text || '';
    templateParamNames.forEach((name, idx) => {
      text = text.replace(`{{${name}}}`, templateParams[idx] || `{{${name}}}`);
    });
    return text;
  };

  const handleSendOutbound = async () => {
    if (newConvSending) return;
    const hasContact = newConvContact?.id;
    const hasPhone = newConvPhone.trim();
    if (!hasContact && !hasPhone) return;

    const windowOpen = !!waState?.windowOpen;
    const sendingFreeform = windowOpen && !forceTemplate && composeBody.trim().length > 0;
    const sendingTemplate = !sendingFreeform && selectedTemplate && !templateParams.some(p => !p.trim());

    if (!sendingFreeform && !sendingTemplate) return;

    setNewConvSending(true);
    try {
      const payload = { ticket_action: ticketAction };
      if (hasContact) {
        payload.contactId = newConvContact.id;
      } else {
        payload.phone = newConvPhone.trim();
        payload.contactName = newConvName.trim() || undefined;
      }
      if (sendingFreeform) {
        payload.body = composeBody.trim();
      } else {
        payload.templateName = selectedTemplate.template_name;
        payload.templateParams = templateParams;
        payload.templateParamNames = templateParamNames;
      }
      const { data } = await api.post('/tickets/wa-compose', payload);
      const actionMsg =
        data.action === 'reopened' ? t('whatsapp.compose.reopened', { n: data.ticket.ticket_number, defaultValue: `Ticket #${data.ticket.ticket_number} reabierto` }) :
        data.action === 'appended' ? t('whatsapp.compose.appended', { n: data.ticket.ticket_number, defaultValue: `Mensaje enviado en #${data.ticket.ticket_number}` }) :
        data.action === 'created_linked' ? t('whatsapp.compose.createdLinked', { n: data.ticket.ticket_number, defaultValue: `Ticket #${data.ticket.ticket_number} creado (linkeado)` }) :
        t('whatsapp.compose.created', { n: data.ticket.ticket_number, defaultValue: `Ticket #${data.ticket.ticket_number} creado` });
      toast.success(actionMsg);
      closeNewConv();
      fetchConversations();
      if (data.ticket?.id) selectConversation({ id: data.ticket.id });
    } catch (err) {
      const msg = err.response?.data?.error || t('whatsapp.compose.sendFailed', 'Error al enviar');
      toast.error(msg);
    }
    setNewConvSending(false);
  };

  // Cleanup recording on unmount or conv change
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) cancelRecording();
    };
  }, [selectedConv]); // eslint-disable-line

  // ¿El cliente pidió una llamada y todavía no se la atendimos? Se deduce del hilo y no
  // de un evento de socket a propósito: así sobrevive a recargar la página y a abrir el
  // bubble más tarde. Vale hasta que el agente conteste algo — si ya respondió, se asume
  // que lo atendió o lo resolvió por texto.
  const pedido = useMemo(() => {
    if (!messages || !messages.length) return null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.is_private) continue;
      if (m.author_type === 'agent') return null;          // ya le contestamos
      // Un intento de atender también cierra el pedido, aunque el cliente no haya
      // llegado a conectarse: esas filas las deja el socket al terminar la sesión
      // (channel 'call' / 'assist'). Sin esto el aviso quedaba pegado para siempre
      // después de un intento fallido. Si el cliente vuelve a ofrecer, su mensaje
      // nuevo queda más abajo y el aviso reaparece.
      if (m.channel === 'assist' || m.channel === 'call') return null;
      if (m.author_type !== 'contact') continue;
      const txt = m.body_text || '';
      if (/solicita una videollamada/i.test(txt)) return { tipo: 'video', id: m.id };
      if (/solicita una llamada/i.test(txt)) return { tipo: 'audio', id: m.id };
      if (/ofrece compartir su pantalla/i.test(txt)) return { tipo: 'screen', id: m.id };
    }
    return null;
  }, [messages]);
  // El agente puede descartar el aviso a mano (antes no había forma de sacarlo).
  // Se recuerda por mensaje: si el cliente vuelve a pedir, es otro id y reaparece.
  const [pedidosDescartados, setPedidosDescartados] = useState(() => new Set());
  const pedidoDeLlamada = pedido && !pedidosDescartados.has(pedido.id) ? pedido.tipo : null;

  // Cartelito de "el cliente está escribiendo", sólo en los chats embebidos.
  const avisoEscribiendo = (clienteEscribiendo && selectedConv && !esWhatsApp(selectedConv))
    ? (
      <div style={{ padding: '2px 10px', fontSize: 12, color: 'var(--c-text-muted)', fontStyle: 'italic' }}>
        {t('integrationChat.clientTyping', 'El cliente está escribiendo…')}
      </div>
    ) : null;

  // ── Auto-resize textarea + @mention detection ──
  const handleTextareaInput = (e) => {
    const val = e.target.value;
    setMsgText(val);
    // En los chats embebidos el cliente está mirando la ventana: avisarle que estamos
    // escribiendo es la diferencia entre "no me contestan" y "ahí viene". En WhatsApp no
    // aplica: Meta no expone el indicador de escritura saliente.
    if (selectedConv && !esWhatsApp(selectedConv)) avisarQueEscribo(selectedConv.id);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 100) + 'px';

    // Detect @mention trigger
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery({ query: atMatch[1], startPos: cursorPos - atMatch[0].length });
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (agent) => {
    if (!mentionQuery || !agent) return;
    const before = msgText.slice(0, mentionQuery.startPos);
    const after = msgText.slice(textareaRef.current?.selectionStart || mentionQuery.startPos + mentionQuery.query.length + 1);
    const mentionText = agent.last_name ? `@"${agent.first_name} ${agent.last_name}" ` : `@${agent.first_name} `;
    setMsgText(before + mentionText + after);
    setMentionQuery(null);
    setMentionIndex(0);
    textareaRef.current?.focus();
  };

  // ── Filter conversations ──
  const filtered = search
    ? conversations.filter(c => {
        const q = search.toLowerCase();
        return (c.contact_name || '').toLowerCase().includes(q)
          || (c.contact_phone || '').toLowerCase().includes(q)
          || (c.company_name || '').toLowerCase().includes(q);
      })
    : conversations;

  // ── Render attachment in message ──
  const renderAttachment = (att, msgAttachments) => {
    const url = `/uploads/attachments/${att.filename}`;
    const mime = att.mime_type || '';
    if (mime.startsWith('image/')) {
      const openLightbox = () => {
        const images = (msgAttachments || [att])
          .filter(a => (a.mime_type || '').startsWith('image/'))
          .map(a => ({ src: `/uploads/attachments/${a.filename}`, alt: a.original_name || a.filename }));
        const startIndex = Math.max(0, images.findIndex(i => i.src === url));
        setLightboxImage({ images, startIndex: startIndex === -1 ? 0 : startIndex });
      };
      return (
        <button
          key={att.id}
          type="button"
          onClick={openLightbox}
          className="wa-bubble-inline-img"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'zoom-in' }}
        >
          <img src={url} alt={att.original_name} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, display: 'block' }} />
        </button>
      );
    }
    if (mime.startsWith('audio/') || mime === 'audio/ogg') {
      return (
        <div key={att.id}>
          <audio controls src={url} style={{ maxWidth: '100%', marginTop: 4 }} />
          {att.transcription ? (
            <div style={{ fontSize: 12, color: 'var(--c-text-muted)', fontStyle: 'italic', marginTop: 2, lineHeight: 1.45 }}>
              💬 {att.transcription}
            </div>
          ) : null}
        </div>
      );
    }
    if (mime.startsWith('video/')) {
      return <video key={att.id} controls src={url} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6, marginTop: 4 }} />;
    }
    return (
      <a key={att.id} href={url} target="_blank" rel="noreferrer" className="wa-bubble-file-link">
        <FileText size={14} /> {att.original_name || att.filename}
      </a>
    );
  };

  // ── Date separators ──
  const getDateKey = (dateStr) => new Date(dateStr).toDateString();

  // ── Portal render ──
  return ReactDOM.createPortal(
    <>
      {joinCall && (
        <VoiceCallWidget
          key={joinCall.id}
          join={{ id: joinCall.id, mode: joinCall.mode }}
          ticketId={joinCall.ticket_id}
          contactName={joinCall.contact_name}
          onClose={() => setJoinCall(null)}
        />
      )}
      {/* FAB */}
      <button
        ref={fabRef}
        className={`wa-bubble-fab${!open && totalUnread > 0 ? ' has-unread' : ''}`}
        style={{
          position: 'fixed', left: dragPos.x, top: dragPos.y,
          right: 'auto', bottom: 'auto',
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease',
          touchAction: 'none',
        }}
        onClick={() => {
          if (wasDragged.current) return;
          if (open) {
            track('whatsapp', 'bubble_close', null, {
              durationMs: openedAtRef.current ? Date.now() - openedAtRef.current : null,
              result: usedRef.current ? 'ok' : 'abandon',
            });
            openedAtRef.current = null;
            usedRef.current = false;
          } else {
            openedAtRef.current = Date.now();
            track('whatsapp', 'bubble_open', totalUnread > 0 ? 'con_sin_leer' : 'sin_pendientes', { result: 'ok' });
          }
          setOpen(prev => !prev);
        }}
        title={t('whatsapp.bubble.title', 'WhatsApp')}
        {...dragHandlers}
      >
        {open ? <X size={24} color="#fff" /> : <MessageCircle size={24} color="#fff" />}
        {!open && totalUnread > 0 && (
          <span className="wa-bubble-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div className="wa-bubble-panel" style={bubblePanelStyle(dragPos, { width: 380, height: 480, minHeight: 300 })} onKeyDown={e => e.stopPropagation()}>
          {!selectedConv ? (
            /* ── Conversation List or New Conversation ── */
            showNewConv ? (
              /* ── New Conversation Panel ── */
              <>
                <div className="wa-bubble-header wa-bubble-chat-header">
                  <button className="wa-bubble-back-btn" onClick={closeNewConv}>
                    <ArrowLeft size={18} />
                  </button>
                  <span style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>Nueva conversación</span>
                </div>

                <div className="wa-bubble-list" style={{ padding: 12, gap: 10, display: 'flex', flexDirection: 'column' }}>
                  {/* Step 1: Contact search or manual entry */}
                  {!newConvContact && !newConvManual && (
                    <>
                      <div className="wa-bubble-search" style={{ margin: 0 }}>
                        <Search size={14} style={{ color: '#8696a0', flexShrink: 0 }} />
                        <input
                          type="text"
                          placeholder="Buscar contacto por nombre, teléfono, empresa..."
                          value={newConvSearch}
                          onChange={e => handleNewConvSearch(e.target.value)}
                          autoFocus
                        />
                      </div>
                      {newConvResults.length > 0 && (
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {newConvResults.map(c => (
                            <div
                              key={c.id}
                              className="wa-bubble-conv-item"
                              onClick={() => selectNewConvContact(c)}
                              style={{ padding: '8px 10px' }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                              <div style={{ fontSize: 12, color: '#8696a0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Phone size={11} /> <span style={{ fontWeight: 500, color: '#d1d5db' }}>{c.phone}</span>
                                {c.company_name && (
                                  <span
                                    className="wa-bubble-company-pill"
                                    style={{
                                      borderColor: c.label_color || '#6b7280',
                                      backgroundColor: (c.label_color || '#6b7280') + '18',
                                      color: c.label_color || '#6b7280',
                                      marginLeft: 4,
                                    }}
                                  >
                                    {c.company_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        onClick={() => setNewConvManual(true)}
                        style={{
                          background: 'none', border: 'none', color: '#25d366',
                          fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                          padding: '4px 0', textDecoration: 'underline',
                        }}
                      >
                        Usar número manual
                      </button>
                    </>
                  )}

                  {/* Selected contact chip */}
                  {newConvContact && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                      background: 'rgba(37,211,102,0.08)', borderRadius: 8, border: '1px solid rgba(37,211,102,0.2)',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{newConvContact.name}</div>
                        <div style={{ fontSize: 12, color: '#8696a0', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Phone size={11} /> <span style={{ fontWeight: 500, color: '#d1d5db' }}>{newConvContact.phone}</span>
                        </div>
                      </div>
                      <button onClick={() => { setNewConvContact(null); setNewConvSearch(''); setNewConvResults([]); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                      >
                        <X size={14} style={{ color: '#8696a0' }} />
                      </button>
                    </div>
                  )}

                  {/* Manual phone entry */}
                  {newConvManual && !newConvContact && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input
                        type="tel"
                        placeholder="Teléfono (ej: +54 9 11 1234-5678)"
                        value={newConvPhone}
                        onChange={e => setNewConvPhone(e.target.value)}
                        className="wa-bubble-textarea"
                        style={{ padding: '8px 10px', borderRadius: 8, height: 'auto', fontSize: 13 }}
                        autoFocus
                      />
                      <input
                        type="text"
                        placeholder="Nombre (opcional)"
                        value={newConvName}
                        onChange={e => setNewConvName(e.target.value)}
                        className="wa-bubble-textarea"
                        style={{ padding: '8px 10px', borderRadius: 8, height: 'auto', fontSize: 13 }}
                      />
                      <button
                        onClick={() => { setNewConvManual(false); setNewConvPhone(''); setNewConvName(''); }}
                        style={{
                          background: 'none', border: 'none', color: '#8696a0',
                          fontSize: 11, cursor: 'pointer', textAlign: 'left', padding: '2px 0',
                        }}
                      >
                        ← Volver a búsqueda
                      </button>
                    </div>
                  )}

                  {/* Step 2: window-aware composer */}
                  {(newConvContact || (newConvManual && newConvPhone.trim())) && (() => {
                    const windowOpen = !!waState?.windowOpen;
                    const openT = waState?.openTicket;
                    const lastT = waState?.lastTicket;
                    const canReopen = !!waState?.canAutoReopen;
                    const showFreeform = windowOpen && !forceTemplate;
                    // The ticket whose inbound is keeping Meta's 24h window open.
                    // Surface it when it's DIFFERENT from the ticket the banner
                    // already shows — that's the surprising case ("no tengo chat
                    // abierto con este contacto, ¿por qué hay freeform?").
                    const winSrc = waState?.windowSourceTicket;
                    const winSrcDisplayedAlready = winSrc && (
                      (openT && winSrc.id === openT.id) ||
                      (!openT && lastT && winSrc.id === lastT.id)
                    );
                    const showWindowSource = windowOpen && winSrc && !winSrcDisplayedAlready;
                    const freeformReady = composeBody.trim().length > 0;
                    const templateReady = selectedTemplate && !templateParams.some(p => !p.trim());
                    const sendDisabled = newConvSending || waStateLoading ||
                      (showFreeform ? !freeformReady : !templateReady);

                    return (
                      <>
                        {/* Context banner — last ticket + window state */}
                        {waStateLoading && (
                          <div style={{ fontSize: 12, color: '#8696a0', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Loader2 size={12} className="spin" /> {t('whatsapp.compose.loadingState', 'Consultando estado...')}
                          </div>
                        )}
                        {!waStateLoading && waState && (openT || lastT) && (
                          <div style={{
                            padding: '8px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.45,
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(134,150,160,0.2)',
                            display: 'flex', flexDirection: 'column', gap: 4,
                          }}>
                            {openT ? (
                              <div>
                                <SessionDot expiresAt={waState.windowExpiresAt} />
                                {t('whatsapp.compose.openTicket', { n: openT.ticket_number, defaultValue: `Ticket abierto #${openT.ticket_number}` })}
                                <span style={{ color: '#8696a0' }}>
                                  {' · '}
                                  {windowOpen
                                    ? t('whatsapp.compose.windowOpen', { time: formatSessionCountdown(waState.windowExpiresAt).text, defaultValue: `ventana abierta (${formatSessionCountdown(waState.windowExpiresAt).text})` })
                                    : t('whatsapp.compose.windowClosed', 'ventana de 24h cerrada')}
                                </span>
                                <div style={{ color: '#8696a0', fontSize: 11 }}>
                                  {ticketAction === 'split'
                                    ? t('whatsapp.compose.willSplit', { n: openT.ticket_number, defaultValue: `Se creará un ticket separado linkeado a #${openT.ticket_number}.` })
                                    : t('whatsapp.compose.willAppend', 'Se agregará el mensaje a este ticket.')}
                                </div>
                                {showWindowSource && (
                                  <div style={{ color: '#8696a0', fontSize: 11, fontStyle: 'italic' }}>
                                    {t('whatsapp.compose.windowSource', {
                                      n: winSrc.ticket_number,
                                      when: formatWindowSourceWhen(winSrc.last_customer_activity_at),
                                      defaultValue: `Ventana abierta por #${winSrc.ticket_number} — último mensaje del cliente ${formatWindowSourceWhen(winSrc.last_customer_activity_at)}`,
                                    })}
                                  </div>
                                )}
                              </div>
                            ) : lastT && (
                              <div>
                                <SessionDot expiresAt={waState.windowExpiresAt} />
                                {t('whatsapp.compose.lastTicket', { n: lastT.ticket_number, status: lastT.status, defaultValue: `Último ticket #${lastT.ticket_number} (${lastT.status})` })}
                                <span style={{ color: '#8696a0' }}>
                                  {' · '}
                                  {windowOpen
                                    ? t('whatsapp.compose.windowOpen', { time: formatSessionCountdown(waState.windowExpiresAt).text, defaultValue: `ventana abierta (${formatSessionCountdown(waState.windowExpiresAt).text})` })
                                    : t('whatsapp.compose.windowClosed', 'ventana de 24h cerrada')}
                                </span>
                                {showWindowSource && (
                                  <div style={{ color: '#8696a0', fontSize: 11, fontStyle: 'italic' }}>
                                    {t('whatsapp.compose.windowSource', {
                                      n: winSrc.ticket_number,
                                      when: formatWindowSourceWhen(winSrc.last_customer_activity_at),
                                      defaultValue: `Ventana abierta por #${winSrc.ticket_number} — último mensaje del cliente ${formatWindowSourceWhen(winSrc.last_customer_activity_at)}`,
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Append / Split toggle when there's an open ticket */}
                        {openT && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              onClick={() => setTicketAction('auto')}
                              style={{
                                flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                border: ticketAction !== 'split' ? '1px solid #25d366' : '1px solid rgba(134,150,160,0.3)',
                                background: ticketAction !== 'split' ? 'rgba(37,211,102,0.15)' : 'transparent',
                                color: ticketAction !== 'split' ? '#25d366' : '#d1d5db',
                              }}
                            >
                              {t('whatsapp.compose.appendBtn', { n: openT.ticket_number, defaultValue: `Continuar en #${openT.ticket_number}` })}
                            </button>
                            <button
                              type="button"
                              onClick={() => setTicketAction('split')}
                              style={{
                                flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                border: ticketAction === 'split' ? '1px solid #25d366' : '1px solid rgba(134,150,160,0.3)',
                                background: ticketAction === 'split' ? 'rgba(37,211,102,0.15)' : 'transparent',
                                color: ticketAction === 'split' ? '#25d366' : '#d1d5db',
                              }}
                            >
                              {t('whatsapp.compose.splitBtn', { n: openT.ticket_number, defaultValue: `Nuevo ticket (link #${openT.ticket_number})` })}
                            </button>
                          </div>
                        )}

                        {/* Reopen / New toggle — only when there's a choice to make */}
                        {!openT && lastT && (lastT.status === 'resolved' || lastT.status === 'closed') && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              type="button"
                              onClick={() => setTicketAction('reopen')}
                              disabled={lastT.status === 'closed'}
                              style={{
                                flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                                cursor: lastT.status === 'closed' ? 'not-allowed' : 'pointer',
                                border: ticketAction === 'reopen' ? '1px solid #25d366' : '1px solid rgba(134,150,160,0.3)',
                                background: ticketAction === 'reopen' ? 'rgba(37,211,102,0.15)' : 'transparent',
                                color: ticketAction === 'reopen' ? '#25d366' : '#d1d5db',
                                opacity: lastT.status === 'closed' ? 0.4 : 1,
                              }}
                              title={lastT.status === 'closed' ? t('whatsapp.compose.closedCannotReopen', 'Los tickets cerrados no se pueden reabrir — se creará uno nuevo linkeado') : ''}
                            >
                              {t('whatsapp.compose.reopenBtn', { n: lastT.ticket_number, defaultValue: `Reabrir #${lastT.ticket_number}` })}
                              {!canReopen && lastT.status === 'resolved' && (
                                <span style={{ fontSize: 10, opacity: 0.7, display: 'block', fontWeight: 400 }}>
                                  {t('whatsapp.compose.reopenOutsideWindow', 'fuera de ventana auto')}
                                </span>
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => setTicketAction('new')}
                              style={{
                                flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                border: ticketAction === 'new' ? '1px solid #25d366' : '1px solid rgba(134,150,160,0.3)',
                                background: ticketAction === 'new' ? 'rgba(37,211,102,0.15)' : 'transparent',
                                color: ticketAction === 'new' ? '#25d366' : '#d1d5db',
                              }}
                            >
                              {t('whatsapp.compose.newLinkedBtn', { n: lastT.ticket_number, defaultValue: `Nuevo ticket (link #${lastT.ticket_number})` })}
                            </button>
                          </div>
                        )}

                        {/* Freeform textarea — when window open */}
                        {showFreeform && (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#8696a0', marginTop: 4 }}>
                              {t('whatsapp.compose.freeformLabel', 'Mensaje (texto libre)')}
                            </div>
                            <textarea
                              className="wa-bubble-textarea"
                              placeholder={t('whatsapp.compose.freeformPlaceholder', 'Escribí el mensaje...')}
                              value={composeBody}
                              onChange={e => setComposeBody(e.target.value)}
                              rows={4}
                              style={{ padding: '8px 10px', borderRadius: 8, fontSize: 13, minHeight: 80, resize: 'vertical' }}
                              autoFocus
                            />
                            {usableTemplates.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setForceTemplate(true)}
                                style={{
                                  background: 'none', border: 'none', color: '#8696a0',
                                  fontSize: 11, cursor: 'pointer', textAlign: 'left', padding: '2px 0',
                                  textDecoration: 'underline',
                                }}
                              >
                                {t('whatsapp.compose.useTemplateInstead', 'Usar plantilla en su lugar')}
                              </button>
                            )}
                          </>
                        )}

                        {/* Template picker — when window closed OR agent opted in */}
                        {!showFreeform && (
                          <>
                            {windowOpen && forceTemplate && (
                              <button
                                type="button"
                                onClick={() => { setForceTemplate(false); setSelectedTemplate(null); setTemplateParams([]); }}
                                style={{
                                  background: 'none', border: 'none', color: '#8696a0',
                                  fontSize: 11, cursor: 'pointer', textAlign: 'left', padding: '2px 0',
                                  textDecoration: 'underline',
                                }}
                              >
                                {t('whatsapp.compose.backToFreeform', '← Volver a texto libre')}
                              </button>
                            )}
                            {usableTemplates.length > 1 && (
                              <>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#8696a0', marginTop: 4 }}>
                                  {t('whatsapp.compose.templateLabel', 'Plantilla de mensaje')}
                                </div>
                                <select
                                  className="form-select"
                                  value={selectedTemplate?.id || ''}
                                  onChange={e => {
                                    const tpl = templates.find(t => t.id === e.target.value);
                                    if (tpl) handleSelectTemplate(tpl);
                                  }}
                                  style={{ fontSize: 13, padding: '6px 8px', borderRadius: 8 }}
                                >
                                  <option value="">{t('whatsapp.compose.selectTemplate', 'Seleccionar plantilla...')}</option>
                                  {usableTemplates.map(tpl => (
                                    <option key={tpl.id} value={tpl.id}>{tpl.template_name} ({tpl.language})</option>
                                  ))}
                                </select>
                              </>
                            )}

                            {selectedTemplate && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {selectedTemplate.category && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                                    <span style={{
                                      padding: '2px 8px', borderRadius: 10, fontWeight: 600, fontSize: 10,
                                      letterSpacing: 0.3, textTransform: 'uppercase',
                                      background: 'rgba(37,211,102,0.15)', color: '#25d366',
                                      border: '1px solid rgba(37,211,102,0.35)',
                                    }}>
                                      {selectedTemplate.category}
                                    </span>
                                    <span style={{ color: '#8696a0' }}>
                                      {t('whatsapp.compose.utilityHint', 'Plantilla de utilidad — se entrega aunque el cliente no haya escrito.')}
                                    </span>
                                  </div>
                                )}
                                <div style={{
                                  padding: '8px 10px', background: 'rgba(255,255,255,0.05)',
                                  borderRadius: 8, fontSize: 12, lineHeight: 1.5,
                                  border: '1px solid rgba(134,150,160,0.2)', whiteSpace: 'pre-wrap',
                                }}>
                                  {getTemplatePreview()}
                                </div>
                                {templateParams.length > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {templateParams.map((val, idx) => {
                                      const paramName = templateParamNames[idx] || '';
                                      // '1'/'2' cover positional templates (contacto_soporte_v3 is
                                      // {{1}}=nombre, {{2}}=motivo) so the agent gets the same
                                      // labelled fields as with named-parameter templates.
                                      const isMotivo = ['motivo', 'reason', 'mensaje', '2'].includes(paramName.toLowerCase());
                                      const isNombre = ['nombre', 'name', '1'].includes(paramName.toLowerCase());
                                      return (
                                        <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                          {isMotivo && (
                                            <label style={{ fontSize: 11, color: '#f59e0b', fontWeight: 600 }}>
                                              {t('whatsapp.compose.reasonLabel', 'Escribí el motivo del contacto')}
                                            </label>
                                          )}
                                          <input
                                            type="text"
                                            placeholder={isMotivo ? t('whatsapp.compose.reasonPlaceholder', 'Ej: seguimiento de ticket, consulta técnica...') :
                                                         isNombre ? t('whatsapp.compose.namePlaceholder', 'Nombre del contacto') :
                                                         paramName || `Parámetro ${idx + 1}`}
                                            value={val}
                                            onChange={e => updateTemplateParam(idx, e.target.value)}
                                            readOnly={isNombre && nameAutoFilled && !!val}
                                            className="wa-bubble-textarea"
                                            style={{
                                              padding: '6px 10px', borderRadius: 8, height: 'auto', fontSize: 12,
                                              ...(isNombre && nameAutoFilled && val ? { opacity: 0.7, cursor: 'default' } : {}),
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}

                        <button
                          className="wa-bubble-send-btn"
                          onClick={handleSendOutbound}
                          disabled={sendDisabled}
                          style={{
                            width: '100%', padding: '10px 16px', borderRadius: 8,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            fontSize: 14, fontWeight: 600, marginTop: 4,
                            opacity: sendDisabled ? 0.5 : 1,
                          }}
                        >
                          {newConvSending ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                          {showFreeform
                            ? t('whatsapp.compose.sendBtn', 'Enviar mensaje')
                            : t('whatsapp.compose.sendTemplateBtn', 'Enviar plantilla')}
                        </button>
                      </>
                    );
                  })()}
                </div>
              </>
            ) : (
            /* ── Conversation List ── */
            <>
              <div className="wa-bubble-header">
                <span style={{ fontWeight: 600, fontSize: 15 }}>{t('whatsapp.bubble.title', 'WhatsApp')}</span>
                <span style={{ fontSize: 12, opacity: 0.8 }}>{conversations.length}</span>
                {clinics.length > 0 && (
                  <div ref={clinicSpot.anchorRef} style={{ position: 'relative', marginLeft: 'auto' }}>
                    <FeatureSpotlight anchorRef={clinicSpot.anchorRef} show={clinicSpot.shouldShow} onDismiss={clinicSpot.dismiss}
                      title={t('whatsapp.bubble.writeToClinic', 'Escribirle a un consultorio')}
                      body={t('whatsapp.bubble.writeToClinicSpot', 'Chateá en vivo con los consultorios de medisoft: lo ven en su bandeja de WhatsApp.')} />
                    <button
                      onClick={() => { clinicSpot.dismiss(); setShowClinics(v => !v); }}
                      title={t('whatsapp.bubble.writeToClinic', 'Escribirle a un consultorio')}
                      style={{
                        background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, padding: '4px 8px',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 12,
                      }}
                    >
                      <Stethoscope size={14} />
                    </button>
                    {showClinics && (
                      <div style={{
                        position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 20, minWidth: 200,
                        background: 'var(--c-surface)', color: 'var(--c-text)', border: '1px solid var(--c-border)',
                        borderRadius: 8, boxShadow: 'var(--shadow)', padding: 4,
                      }}>
                        <div style={{ fontSize: 11, color: 'var(--c-text-muted)', padding: '6px 8px' }}>
                          {t('whatsapp.bubble.writeToClinic', 'Escribirle a un consultorio')}
                        </div>
                        {clinics.map(c => (
                          <button key={c.id} onClick={() => startClinicChat(c)}
                            style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
                              padding: '8px', borderRadius: 6, cursor: 'pointer', color: 'var(--c-text)', fontSize: 13 }}>
                            {c.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={openNewConv}
                  title="Nueva conversación"
                  style={{
                    marginLeft: 'auto', background: 'rgba(255,255,255,0.15)', border: 'none',
                    borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex',
                    alignItems: 'center', gap: 4, color: '#fff', fontSize: 12,
                  }}
                >
                  <Plus size={14} />
                </button>
              </div>

              {/* Search */}
              <div className="wa-bubble-search">
                <Search size={14} style={{ color: '#8696a0', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder={t('whatsapp.bubble.searchPlaceholder', 'Buscar...')}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              {/* List */}
              <div className="wa-bubble-list">
                {filtered.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', color: '#8696a0', fontSize: 13 }}>
                    {t('whatsapp.bubble.noConversations', 'Sin conversaciones')}
                  </div>
                )}
                {filtered.map(conv => (
                  <div
                    key={conv.id}
                    className="wa-bubble-conv-item"
                    onClick={() => selectConversation(conv)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      {esWhatsApp(conv) && <SessionDot expiresAt={conv.wa_session_expires_at} />}
                      {conv.contact_avatar ? (
                        <img src={conv.contact_avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                          background: 'rgba(37,211,102,0.15)', color: '#25d366',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: 14,
                        }}>
                          {((conv.contact_name || conv.contact_phone || '?')[0] || '?').toUpperCase()}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {conv.contact_name || conv.contact_phone}
                          </span>
                          <span style={{ fontSize: 11, color: '#8696a0', flexShrink: 0, marginLeft: 8 }}>
                            {formatTime(conv.last_message_at)}
                          </span>
                        </div>
                        {conv.contact_phone && conv.contact_name && conv.contact_name !== conv.contact_phone && (
                          <div style={{ fontSize: 11, color: '#8696a0', marginTop: 1 }}>{conv.contact_phone}</div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                          <span style={{ fontSize: 12, color: '#8696a0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {conv.last_message_author === 'agent' && <Check size={12} style={{ marginRight: 2, verticalAlign: 'middle' }} />}
                            {conv.last_message_text ? conv.last_message_text.substring(0, 60) : '...'}
                          </span>
                          {conv.unread_count > 0 && (
                            <span className="wa-bubble-unread-pill">{conv.unread_count}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                          <PildoraOrigen conv={conv} />
                          {conv.company_name && (
                            <span
                              className="wa-bubble-company-pill"
                              style={{
                                borderColor: conv.label_color || '#6b7280',
                                backgroundColor: (conv.label_color || '#6b7280') + '18',
                                color: conv.label_color || '#6b7280',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                              }}
                            >
                              {conv.company_name && (
                                <CompanyLogo name={conv.company_name} logoUrl={conv.company_logo_url} companyId={conv.company_id} size={12} tile={false} />
                              )}
                              {conv.company_name}
                            </span>
                          )}
                          {conv.wa_number_label && (
                            <span style={{ fontSize: 10, color: '#8696a0' }}>
                              {t('whatsapp.bubble.via', 'vía')} {conv.wa_number_label}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
            )
          ) : (
            /* ── Chat View ── */
            <>
              <div className="wa-bubble-header wa-bubble-chat-header">
                <button className="wa-bubble-back-btn" onClick={() => { setSelectedConv(null); setMessages([]); setShowCollabPicker(false); setEditingSubject(false); }}>
                  <ArrowLeft size={18} />
                </button>
                {selectedConv.contact_avatar ? (
                  <img src={selectedConv.contact_avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, marginRight: 8 }} />
                ) : (
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0, marginRight: 8,
                    background: 'rgba(255,255,255,0.18)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 14,
                  }}>
                    {((selectedConv.contact_name || selectedConv.contact_phone || '?')[0] || '?').toUpperCase()}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0, cursor: selectedConv.contact_id ? 'pointer' : 'default' }}
                  onClick={() => { if (selectedConv.contact_id) { navigate(`/companies?contactId=${selectedConv.contact_id}`); setOpen(false); } }}
                  title={selectedConv.contact_id ? t('whatsapp.bubble.openContact', 'Ver contacto') : undefined}>
                  <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedConv.contact_name || selectedConv.contact_phone}</span>
                    <PildoraOrigen conv={selectedConv} />
                    {origenDe(selectedConv) && selectedConv.contact_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); buscarCandidatos(); }}
                        title={t('whatsapp.link.tooltip', '¿Esta persona ya la tenemos con otro contacto?')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.7, padding: 0, display: 'inline-flex' }}
                      >
                        <Link2 size={13} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 11, opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
                    {selectedConv.ticket_number && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          // El bubble NO se cierra: se abre el ticket y la conversación queda
                          // a la vista. Cerrarlo obligaba a volver a abrirlo y a buscar el chat
                          // de nuevo justo cuando uno quiere leer las dos cosas a la vez.
                          //
                          // Se navega por NÚMERO, que es la URL canónica del ticket desde la
                          // migración de agosto (por eso existe el router.param que traduce
                          // número→uuid en el backend). Importa acá más que en otros lados:
                          // como el bubble ya no se cierra, la barra de direcciones queda a la
                          // vista y esa URL es la que se copia y se pega en un chat.
                          navigate(`/tickets/${selectedConv.ticket_number || selectedConv.id}`);
                        }}
                        style={{ cursor: 'pointer', color: '#60a5fa', fontWeight: 600 }}
                        title={t('whatsapp.bubble.openTicket', 'Abrir ticket')}
                      >
                        #{selectedConv.ticket_number}
                      </span>
                    )}
                    {selectedConv.ticket_number && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSubjectDraft((selectedConv.subject || '').replace(/^WhatsApp:\s*/i, ''));
                          setEditingSubject(v => !v);
                        }}
                        title={t('whatsapp.bubble.editSubject', 'Editar título del ticket')}
                        style={{
                          background: 'transparent', border: 'none', padding: 2,
                          cursor: 'pointer', color: 'inherit', opacity: 0.85,
                          display: 'inline-flex', alignItems: 'center',
                        }}
                      >
                        <Pencil size={11} />
                      </button>
                    )}
                    {selectedConv.ticket_number && <span>·</span>}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedConv.contact_phone}
                      {selectedConv.company_name && ` · ${selectedConv.company_name}`}
                    </span>
                    {selectedConv.company_suspended && (
                      <span title={t('tickets.suspendedBadgeTitle')} style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
                        background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.35)',
                        color: '#ef4444', borderRadius: 6, padding: '1px 6px', fontSize: 10, fontWeight: 700,
                      }}>⏸ {t('tickets.suspendedBadge')}</span>
                    )}
                    {/* Antes acá decía sólo "· 3 agentes": el número, no QUIÉNES. Los nombres
                        estaban detrás del panel de colaboradores y por eso Santi reportó que
                        "no se pueden ver las colaboraciones" — la función existía, no se veía.
                        Ahora se leen de una y siguen siendo el atajo para abrir el panel. */}
                    {collaborators.length > 0 && (
                      <span
                        onClick={openCollabPicker}
                        title={t('whatsapp.collaboratorsTooltip', 'Ver y gestionar colaboradores')}
                        style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {' · '}
                        <Users size={10} style={{ verticalAlign: '-1px', marginRight: 2 }} />
                        {collaborators.slice(0, 2).map(c => c.first_name || c.name).filter(Boolean).join(', ')}
                        {collaborators.length > 2 && ` +${collaborators.length - 2}`}
                      </span>
                    )}
                  </div>
                </div>
                {selectedConv.company_id && (
                  <button
                    ref={clientCtxSpotlight.anchorRef}
                    className="wa-bubble-open-ticket-btn"
                    onClick={() => { clientCtxSpotlight.dismiss(); setClientContextOpen(true); }}
                    title={t('clientContext.openTooltip')}
                  >
                    <BookUser size={15} />
                  </button>
                )}
                <button
                  ref={voiceCallSpotlight.anchorRef}
                  className="wa-bubble-open-ticket-btn"
                  onClick={() => { voiceCallSpotlight.dismiss(); setVoiceCallMode('audio'); setVoiceCallOpen(true); }}
                  title={t('voiceCall.callTooltip', 'Llamar por voz')}
                >
                  <PhoneCall size={16} />
                </button>
                {selectedConv && !esWhatsApp(selectedConv) && (
                  <button
                    className="wa-bubble-open-ticket-btn"
                    onClick={() => { setVoiceCallMode('video'); setVoiceCallOpen(true); }}
                    title={t('voiceCall.videoTooltip', 'Videollamada')}
                  >
                    <Video size={16} />
                  </button>
                )}
                {selectedConv && !esWhatsApp(selectedConv) && (
                  <button
                    className="wa-bubble-open-ticket-btn"
                    disabled={abriendoAsistencia}
                    onClick={abrirAsistencia}
                    title={t('whatsapp.assist.tooltip', 'Pedir la pantalla del cliente')}
                  >
                    <MonitorUp size={16} />
                  </button>
                )}
                {dupCandidates.length > 0 && (
                  <button
                    className="wa-bubble-open-ticket-btn"
                    onClick={() => setShowMerge(true)}
                    title={t('whatsapp.possibleDuplicateHint')}
                    style={{ width: 'auto', padding: '0 8px', gap: 4, display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700 }}
                  >
                    <GitMerge size={14} />
                    <span>{t('whatsapp.possibleDuplicate')}</span>
                  </button>
                )}
                <button
                  className="wa-bubble-open-ticket-btn"
                  onClick={openCollabPicker}
                  title="Colaboradores"
                  style={showCollabPicker ? { background: 'rgba(255,255,255,0.2)', opacity: 1 } : undefined}
                >
                  <UserPlus size={16} />
                </button>
              </div>
              <FeatureSpotlight
                anchorRef={voiceCallSpotlight.anchorRef}
                show={voiceCallSpotlight.shouldShow}
                onDismiss={voiceCallSpotlight.dismiss}
                title={t('voiceCall.spotlightTitle', 'Llamá al cliente por voz')}
                body={t('voiceCall.spotlightBody', 'WhatsApp no permite llamadas a empresas: este botón le manda al cliente un enlace seguro para hablar por voz desde el navegador. Queda grabado (solo admins escuchan).')}
                placement="bottom"
              />
              <FeatureSpotlight
                anchorRef={clientCtxSpotlight.anchorRef}
                show={clientCtxSpotlight.shouldShow && !voiceCallSpotlight.shouldShow}
                onDismiss={clientCtxSpotlight.dismiss}
                title={t('clientContext.spotlightTitle')}
                body={t('clientContext.spotlightBody')}
                placement="bottom"
              />
              {showMerge && selectedConv && (
                <MergeTicketsModal
                  selectedTickets={[
                    { id: selectedConv.id, ticket_number: selectedConv.ticket_number, subject: selectedConv.subject,
                      contact_name: selectedConv.contact_name, status: selectedConv.status },
                    ...dupCandidates.map(c => ({ id: c.id, ticket_number: c.ticket_number, subject: c.subject,
                      contact_name: c.contact_name, status: c.status })),
                  ]}
                  onClose={() => setShowMerge(false)}
                  onMerged={() => { setShowMerge(false); setSelectedConv(null); setMessages([]); fetchConversations(); }}
                />
              )}
              {clientContextOpen && selectedConv?.company_id && (
                <ClientContextPanel
                  companyId={selectedConv.company_id}
                  contactId={selectedConv.contact_id}
                  onClose={() => setClientContextOpen(false)}
                />
              )}
              {voiceCallOpen && selectedConv && (
                <VoiceCallWidget
                  ticketId={selectedConv.id}
                  contactName={selectedConv.contact_name || selectedConv.contact_phone}
                  mode={voiceCallMode}
                  onClose={() => setVoiceCallOpen(false)}
                />
              )}

              {/* Subject editor (inline) */}
              {editingSubject && (
                <div style={{
                  padding: '8px 12px', background: 'rgba(99,102,241,0.08)',
                  borderBottom: '1px solid rgba(99,102,241,0.25)',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <Pencil size={13} style={{ color: '#6366f1', flexShrink: 0 }} />
                  <input
                    autoFocus
                    type="text"
                    className="form-input"
                    value={subjectDraft}
                    onChange={(e) => setSubjectDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saveSubject(); }
                      else if (e.key === 'Escape') { e.preventDefault(); setEditingSubject(false); }
                    }}
                    placeholder={t('tickets.subjectPlaceholder', 'Título del ticket')}
                    disabled={subjectSaving}
                    style={{ flex: 1, fontSize: 12, padding: '4px 8px', height: 28 }}
                  />
                  <button
                    onClick={saveSubject}
                    disabled={subjectSaving || !subjectDraft.trim()}
                    style={{
                      background: '#6366f1', color: '#fff', border: 'none',
                      borderRadius: 4, padding: '4px 10px', fontSize: 11, fontWeight: 600,
                      cursor: subjectSaving || !subjectDraft.trim() ? 'not-allowed' : 'pointer',
                      opacity: subjectSaving || !subjectDraft.trim() ? 0.5 : 1,
                    }}
                  >
                    {subjectSaving ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : t('common.save', 'Guardar')}
                  </button>
                  <button
                    onClick={() => setEditingSubject(false)}
                    disabled={subjectSaving}
                    style={{
                      background: 'transparent', border: '1px solid rgba(99,102,241,0.4)',
                      color: '#6366f1', borderRadius: 4, padding: '4px 8px', fontSize: 11,
                      cursor: 'pointer',
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              )}

              {/* No-company prompt */}
              {selectedConv && !selectedConv.company_name && selectedConv.contact_id && (
                <div style={{
                  padding: '6px 12px', background: 'rgba(234,179,8,0.08)',
                  borderBottom: '1px solid rgba(234,179,8,0.2)',
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 12
                }}>
                  <Building2 size={13} style={{ color: '#eab308', flexShrink: 0 }} />
                  {!companyList ? (
                    <>
                      <span style={{ color: '#eab308', flex: 1 }}>Sin empresa asignada</span>
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await api.get('/companies?status=active');
                            setCompanyList(data.companies || data);
                          } catch { toast.error(t('whatsapp.errorLoadingCompanies')); }
                        }}
                        style={{
                          background: 'none', border: '1px solid rgba(234,179,8,0.4)', borderRadius: 4,
                          color: '#eab308', fontSize: 11, fontWeight: 600, padding: '2px 8px', cursor: 'pointer'
                        }}
                      >
                        Asignar
                      </button>
                    </>
                  ) : (
                    <select
                      className="form-select"
                      autoFocus
                      disabled={assigningCompany}
                      defaultValue=""
                      onChange={async (e) => {
                        const companyId = e.target.value;
                        if (!companyId) return;
                        setAssigningCompany(true);
                        try {
                          // Update contact's company (cascades to tickets + company_contacts)
                          const contactRes = await api.get(`/contacts/${selectedConv.contact_id}`);
                          const c = contactRes.data;
                          await api.put(`/contacts/${selectedConv.contact_id}`, { ...c, company_id: companyId });
                          const company = companyList.find(co => co.id === companyId);
                          // Use fantasy_name || name to match what the API returns via COALESCE
                          const companyName = company?.fantasy_name || company?.name;
                          setSelectedConv(prev => ({ ...prev, company_name: companyName, company_id: companyId, label_color: company?.label_color }));
                          // Update the conversations list directly to avoid race condition with the
                          // 10-second poll interval (an in-flight poll request would return stale null data
                          // and overwrite the optimistic update via fetchConversations sync)
                          setConversations(prev => prev.map(conv =>
                            conv.id === selectedConv.id
                              ? { ...conv, company_name: companyName, company_id: companyId, label_color: company?.label_color }
                              : conv
                          ));
                          setCompanyList(null);
                          toast.success(t('whatsapp.companyAssigned'));
                        } catch { toast.error(t('whatsapp.errorAssigningCompany')); }
                        finally { setAssigningCompany(false); }
                      }}
                      onBlur={() => setCompanyList(null)}
                      style={{ flex: 1, fontSize: 12, padding: '3px 6px', height: 28 }}
                    >
                      <option value="">Seleccionar empresa...</option>
                      {companyList.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
                    </select>
                  )}
                </div>
              )}

              {/* Collaborator picker */}
              {showCollabPicker && (
                <div className="wa-bubble-collab-panel">
                  <div className="wa-bubble-collab-title">
                    <Users size={14} /> Colaboradores
                  </div>
                  {/* Current collaborators */}
                  {collaborators.map(c => (
                    <div key={c.user_id} className="wa-bubble-collab-item">
                      <span className="wa-bubble-collab-dot" style={{ background: presenceColor(presenceStatus(c)) }} />
                      <span style={{ flex: 1, fontSize: 12 }}>{c.first_name} {c.last_name}</span>
                      <button className="wa-bubble-collab-remove" onClick={() => removeCollaborator(c.user_id)} title="Quitar">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {/* Available agents to add */}
                  <div className="wa-bubble-collab-title" style={{ marginTop: 4 }}>Agregar agente</div>
                  {agents
                    .filter(a => a.id !== user.id && a.id !== selectedConv.assigned_to && !collaborators.find(c => c.user_id === a.id))
                    .map(a => (
                      <div key={a.id} className="wa-bubble-collab-item wa-bubble-collab-add" onClick={() => addCollaborator(a.id)}>
                        <span className="wa-bubble-collab-dot" style={{ background: presenceColor(presenceStatus(a)) }} />
                        <span style={{ flex: 1, fontSize: 12 }}>{a.first_name} {a.last_name}</span>
                        <UserPlus size={12} style={{ color: '#25d366' }} />
                      </div>
                    ))
                  }
                </div>
              )}

              {/* Session bar + actions */}
              {(() => {
                const session = esWhatsApp(selectedConv) ? formatSessionCountdown(selectedConv.wa_session_expires_at) : null;
                // Los chats de plataformas integradas (consultorios de medisoft, Oxígeno) no
                // tienen ventana de 24 h, pero son tickets igual: la barra tiene que estar
                // para poder resolverlos, ponerlos en espera o pendientes desde acá. Antes la
                // barra —y con ella esos botones— sólo existía para WhatsApp.
                const origen = !session ? origenDe(selectedConv) : null;
                if (!session && !origen) return null;
                return (
                  <div className={`wa-bubble-session-bar session-${session ? session.color : 'green'}`}>
                    {session ? <Clock size={12} /> : <MessageCircle size={12} />}
                    <span style={{ flex: 1 }}>
                      {!session
                        ? t('whatsapp.bubble.integratedChatBar', { origin: origen.label })
                        : session.waiting
                        ? 'Esperando respuesta — no se pueden enviar mensajes libres'
                        : session.expired
                          ? t('whatsapp.bubble.sessionExpired', 'Sesión expirada — usar plantilla')
                          : `${t('whatsapp.bubble.sessionRemaining', 'Sesión')}: ${session.text}`
                      }
                    </span>
                    {selectedConv.status !== 'closed' && selectedConv.status !== 'pending' && (
                      <button className={`wa-bubble-resolve-btn${shiftHeld ? ' wa-bubble-resolve-btn-silent' : ''}`} onClick={(e) => { setResolveSilent(e.shiftKey); setConfirmResolve('pending'); }} title={shiftHeld ? t('tickets.silentModeDesc', 'Modo silencioso — no notificar al cliente') : t('tickets.statuses.pending', 'Pendiente')}>
                        <Timer size={14} />
                      </button>
                    )}
                    {selectedConv.status !== 'closed' && selectedConv.status !== 'on_hold' && (
                      <button className={`wa-bubble-resolve-btn${shiftHeld ? ' wa-bubble-resolve-btn-silent' : ''}`} onClick={(e) => { setResolveSilent(e.shiftKey); setConfirmResolve('on_hold'); }} title={shiftHeld ? t('tickets.silentModeDesc', 'Modo silencioso — no notificar al cliente') : t('tickets.statuses.on_hold', 'En espera')}>
                        <PauseCircle size={14} />
                      </button>
                    )}
                    {selectedConv.status !== 'closed' && selectedConv.status !== 'resolved' && (
                      <button className="wa-bubble-resolve-btn" onClick={handleQuickResolve} title={t('tickets.quickResolveTooltip')} disabled={resolveSubmitting}>
                        <Zap size={14} />
                      </button>
                    )}
                    {selectedConv.status !== 'closed' && selectedConv.status !== 'resolved' && (
                      <button className={`wa-bubble-resolve-btn${shiftHeld ? ' wa-bubble-resolve-btn-silent' : ''}`} onClick={(e) => { setResolveSilent(e.shiftKey); setResolveSubject(selectedConv.subject || ''); setResolveDiagnosis(selectedConv.diagnosis || ''); setResolveSolution(selectedConv.solution || ''); setResolveOverride(''); setConfirmResolve('resolved'); }} title={shiftHeld ? t('tickets.silentModeDesc', 'Modo silencioso — no notificar al cliente') : t('tickets.statuses.resolved', 'Resuelto')}>
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {shiftHeld && (
                      <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600, marginLeft: 4 }}>
                        🤫 {t('tickets.silentMode', 'Silencioso')}
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Confirmation overlay */}
              {confirmResolve && (() => {
                const isResolve = confirmResolve === 'resolved';
                const exempt = selectedConv.kind === 'notification' || selectedConv.status === 'validation';
                const showQuality = isResolve && !exempt;
                const isAdmin = user?.role === 'admin';
                const simpleMode = resolveSimple;
                const minSubj = simpleMode ? 1 : MIN_SUBJECT;
                const minDiag = simpleMode ? 1 : MIN_DIAG;
                const minSol = simpleMode ? 1 : MIN_SOL;
                const subjOk = resolveSubject.trim().length >= minSubj;
                const diagOk = resolveDiagnosis.trim().length >= minDiag;
                const solOk = resolveSolution.trim().length >= minSol;
                const passes = subjOk && diagOk && solOk;
                const overrideOk = isAdmin && resolveOverride.trim().length > 0;
                const okEnabled = !showQuality || passes || overrideOk;
                const closeModal = () => {
                  setConfirmResolve(null);
                  setResolveNotes('');
                  setResolveSilent(false);
                  setResolveSubject('');
                  setResolveDiagnosis('');
                  setResolveSolution('');
                  setResolveOverride('');
                  setResolveSimple(false);
                };
                const ta = (extra = {}) => ({
                  width: '100%', padding: 8, fontSize: 13,
                  border: '1px solid var(--c-border, #d1d7db)', borderRadius: 6,
                  resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                  background: 'var(--c-surface, #fff)', color: 'var(--c-text, #111)',
                  ...extra,
                });
                return (
                <div className="wa-bubble-confirm-overlay">
                  <div className="wa-bubble-confirm-box" style={showQuality ? { width: 360, maxHeight: '90%', overflowY: 'auto' } : undefined}>
                    <p style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600 }}>
                      {isResolve
                        ? t('whatsapp.bubble.confirmResolve', '¿Resolver este ticket?')
                        : confirmResolve === 'on_hold'
                          ? t('whatsapp.bubble.confirmOnHold', '¿Poner en espera este ticket?')
                          : t('whatsapp.bubble.confirmPending', '¿Marcar este ticket como pendiente?')}
                    </p>
                    <p style={{ margin: '0 0 16px', fontSize: 12, color: '#8696a0' }}>
                      #{selectedConv.ticket_number} — {selectedConv.contact_name || selectedConv.contact_phone}
                    </p>
                    {resolveSilent && (
                      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#b45309', fontWeight: 600 }}>
                        {t('tickets.silentModeDesc', 'Modo silencioso — no se notificará al cliente')}
                      </p>
                    )}
                    {showQuality && (
                      <label style={{
                        display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                        padding: '8px 10px', borderRadius: 8, marginBottom: 10,
                        border: `1px solid ${simpleMode ? 'var(--c-brand)' : 'var(--c-border, #d1d7db)'}`,
                        background: simpleMode ? 'rgba(59,130,246,0.08)' : 'transparent',
                      }}>
                        <input
                          type="checkbox"
                          checked={simpleMode}
                          onChange={(e) => setResolveSimple(e.target.checked)}
                          style={{ marginTop: 2 }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{t('tickets.simpleResolveLabel')}</div>
                          <div style={{ fontSize: 11, color: '#8696a0', marginTop: 2 }}>{t('tickets.simpleResolveHint')}</div>
                        </div>
                      </label>
                    )}
                    {showQuality && !simpleMode && selectedConv?.completeness?.values && (
                      <TicketQualityPreview
                        subjectLen={(resolveSubject || '').trim().length}
                        firstInboundLen={selectedConv.completeness.values.first_inbound_len}
                        diagnosisLen={(resolveDiagnosis || '').trim().length}
                        solutionLen={(resolveSolution || '').trim().length}
                        timeMinutes={selectedConv.completeness.values.time_minutes}
                        categoryId={selectedConv.completeness.values.category_id}
                        compact
                      />
                    )}
                    {showQuality && (!resolveDiagnosis.trim() || !resolveSolution.trim()) && (
                      <div style={{ marginBottom: 10 }}>
                        <button
                          type="button"
                          onClick={proposeFieldsFromAI}
                          disabled={aiProposingFields}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            background: 'rgba(99, 102, 241, 0.08)',
                            border: '1px dashed rgba(99, 102, 241, 0.4)',
                            color: '#6366f1', fontWeight: 600, fontSize: 12,
                            borderRadius: 6, padding: '6px 10px',
                            cursor: aiProposingFields ? 'wait' : 'pointer',
                          }}
                        >
                          {aiProposingFields
                            ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />
                            : <Sparkles size={13} />}
                          {aiProposingFields ? t('reviewQueue.generatingProposal') : t('tickets.aiPrefillButton')}
                        </button>
                      </div>
                    )}
                    {showQuality && (
                      <>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '0 0 4px' }}>
                          {t('tickets.resolveSubjectLabel', 'Asunto del ticket')}
                          <span style={{ marginLeft: 6, fontWeight: 400, color: subjOk ? '#16a34a' : '#b45309' }}>
                            {resolveSubject.trim().length}{simpleMode ? '' : `/${MIN_SUBJECT}`}
                          </span>
                        </label>
                        <input
                          type="text"
                          value={resolveSubject}
                          onChange={(e) => setResolveSubject(e.target.value)}
                          placeholder={t('tickets.resolveSubjectPlaceholder', 'Título descriptivo del problema (mín. 10 caracteres)')}
                          style={ta({ marginBottom: 10, resize: 'none' })}
                        />
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '0 0 4px' }}>
                          {t('tickets.diagnosisLabel', 'Diagnóstico / causa raíz')}
                          <span style={{ marginLeft: 6, fontWeight: 400, color: diagOk ? '#16a34a' : '#b45309' }}>
                            {resolveDiagnosis.trim().length}{simpleMode ? '' : `/${MIN_DIAG}`}
                          </span>
                        </label>
                        <textarea
                          value={resolveDiagnosis}
                          onChange={(e) => setResolveDiagnosis(e.target.value)}
                          placeholder={t('tickets.diagnosisPlaceholder', '¿Qué se detectó? Causa raíz, qué estaba fallando, qué encontraste al investigar.')}
                          rows={3}
                          style={ta({ marginBottom: 10 })}
                        />
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '0 0 4px' }}>
                          {t('tickets.solutionLabel', 'Solución aplicada')}
                          <span style={{ marginLeft: 6, fontWeight: 400, color: solOk ? '#16a34a' : '#b45309' }}>
                            {resolveSolution.trim().length}{simpleMode ? '' : `/${MIN_SOL}`}
                          </span>
                        </label>
                        <textarea
                          value={resolveSolution}
                          onChange={(e) => setResolveSolution(e.target.value)}
                          placeholder={t('tickets.solutionPlaceholder', 'Pasos realizados:\n1. \n2. \n3. \n\nResultado: ')}
                          rows={4}
                          style={ta({ marginBottom: 10 })}
                        />
                        {!passes && isAdmin && (
                          <>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '0 0 4px' }}>
                              {t('tickets.qualityOverrideLabel', 'Justificación para resolver sin completar (admin)')}
                            </label>
                            <textarea
                              value={resolveOverride}
                              onChange={(e) => setResolveOverride(e.target.value)}
                              placeholder={t('tickets.qualityOverridePlaceholder', 'Razón por la que no podés completar diagnóstico/solución')}
                              rows={2}
                              style={ta({ marginBottom: 10 })}
                            />
                          </>
                        )}
                      </>
                    )}
                    {isResolve && (
                      <textarea
                        value={resolveNotes}
                        onChange={(e) => setResolveNotes(e.target.value)}
                        placeholder={t('whatsapp.bubble.resolutionNotesPlaceholder', 'Notas de la solución (opcional)')}
                        rows={2}
                        style={ta({ marginBottom: 12 })}
                      />
                    )}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="wa-bubble-confirm-cancel" onClick={closeModal} disabled={resolveSubmitting}>
                        {t('common.cancel', 'Cancelar')}
                      </button>
                      <button
                        className="wa-bubble-confirm-ok"
                        onClick={handleResolveTicket}
                        disabled={resolveSubmitting || !okEnabled}
                        style={!okEnabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                      >
                        {isResolve
                          ? t('tickets.statuses.resolved', 'Resuelto')
                          : confirmResolve === 'on_hold'
                            ? t('tickets.statuses.on_hold', 'En espera')
                            : t('tickets.statuses.pending', 'Pendiente')}
                      </button>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* Messages */}
              {candidatos !== null && (
                <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)', background: 'var(--c-surface-2)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--c-text-muted)', marginBottom: 6 }}>
                    {t('whatsapp.link.title', '¿Ya la tenemos?')}
                  </div>
                  {candidatos.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>{t('whatsapp.link.none', 'No encontramos contactos parecidos')}</div>
                  ) : candidatos.map(c => (
                    <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>{c.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
                          {[c.email, c.phone].filter(Boolean).join(' · ')}
                          {(c.tickets > 0 || c.equipos > 0) && ` — ${c.tickets} tickets, ${c.equipos} equipos`}
                        </div>
                      </div>
                      <button className="btn btn-secondary btn-sm" disabled={vinculando} onClick={() => vincularCon(c)}>
                        {t('whatsapp.link.merge', 'Unificar')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="wa-bubble-messages">
                {loadingMessages && (
                  <div style={{ textAlign: 'center', padding: 20, color: '#8696a0' }}>
                    {t('common.loading', 'Cargando...')}
                  </div>
                )}
                {messages.map((msg, idx) => {
                  const isAgent = msg.author_type === 'agent';
                  const showDateSep = idx === 0 || getDateKey(msg.created_at) !== getDateKey(messages[idx - 1].created_at);
                  const attachments = Array.isArray(msg.attachments) ? msg.attachments.filter(a => a && a.id) : [];

                  return (
                    <React.Fragment key={msg.id}>
                      {showDateSep && (
                        <div className="wa-bubble-date-sep">
                          <span>{formatDateSeparator(msg.created_at)}</span>
                        </div>
                      )}
                      <div className={`wa-bubble-msg ${isAgent ? 'agent' : 'contact'}`}>
                        {msg.is_forwarded && (
                          <div style={{ fontSize: 11, color: '#8696a0', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 3, marginBottom: 2 }}>
                            ↪ {t('tickets.forwardedMessage', 'Reenviado')}
                          </div>
                        )}
                        {msg.is_private && <div className="wa-bubble-private-tag">Privado</div>}
                        {isAgent && msg.first_name && (
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#25d366', marginBottom: 2 }}>
                            {msg.first_name}{msg.last_name ? ` ${msg.last_name.charAt(0)}.` : ''}
                          </div>
                        )}
                        {/* WhatsApp reply-to quote: client (or agent) replied to a specific previous
                            message. Render the original inline so context is visible — even if the
                            quoted message lives in another ticket. */}
                        {msg.reply_to_wa_message_id && (
                          <div style={{
                            margin: '2px 0 6px', padding: '6px 8px',
                            background: 'rgba(37,211,102,0.08)', borderLeft: '3px solid #25d366',
                            borderRadius: 6, fontSize: 12, maxWidth: '100%', overflow: 'hidden'
                          }}>
                            {msg.reply_to ? (
                              <>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#25d366', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <CornerUpLeft size={10} />
                                  {msg.reply_to.author_type === 'agent'
                                    ? `${msg.reply_to.agent_first_name || ''} ${msg.reply_to.agent_last_name || ''}`.trim() || t('tickets.systemBot', 'Agente')
                                    : msg.reply_to.contact_name || t('tickets.replyToContact', 'Cliente')}
                                </div>
                                {(msg.reply_to.attachments || []).map(a => {
                                  const url = `/uploads/attachments/${a.filename}`;
                                  const mime = a.mime_type || '';
                                  if (mime.startsWith('image/')) {
                                    return <img key={a.id} src={url} alt={a.original_name} style={{ maxWidth: 160, maxHeight: 100, borderRadius: 4, display: 'block', marginBottom: 3 }} />;
                                  }
                                  if (mime.startsWith('audio/')) {
                                    return <audio key={a.id} controls src={url} style={{ height: 28, maxWidth: 200, display: 'block', marginBottom: 3 }} />;
                                  }
                                  if (mime.startsWith('video/')) {
                                    return <video key={a.id} controls src={url} style={{ maxWidth: 180, borderRadius: 4, display: 'block', marginBottom: 3 }} />;
                                  }
                                  return (
                                    <a key={a.id} href={url} target="_blank" rel="noreferrer"
                                       style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#8696a0', textDecoration: 'none' }}>
                                      <Paperclip size={10} /> {a.original_name || a.filename}
                                    </a>
                                  );
                                })}
                                {msg.reply_to.body_text && (
                                  <div style={{
                                    color: '#8696a0', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                    display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                  }}>
                                    {msg.reply_to.body_text}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ color: '#8696a0', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 4 }}>
                                <CornerUpLeft size={10} /> {t('tickets.replyOriginalUnavailable', 'Mensaje original no disponible')}
                              </div>
                            )}
                          </div>
                        )}
                        {attachments.map(a => renderAttachment(a, attachments))}
                        {msg.rich_metadata?.kind === 'contacts' && Array.isArray(msg.rich_metadata.contacts) && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {msg.rich_metadata.contacts.map((card, ci) => {
                              const displayName = card.name?.formatted_name
                                || [card.name?.first_name, card.name?.last_name].filter(Boolean).join(' ').trim()
                                || t('whatsapp.vcard.unnamed', 'Contacto');
                              const initial = (displayName[0] || '?').toUpperCase();
                              const orgLine = [card.org?.title, card.org?.company].filter(Boolean).join(' · ');
                              const phones = Array.isArray(card.phones) ? card.phones : [];
                              const emails = Array.isArray(card.emails) ? card.emails : [];
                              const urls = Array.isArray(card.urls) ? card.urls : [];
                              const addresses = Array.isArray(card.addresses) ? card.addresses : [];
                              return (
                                <div key={ci} style={{
                                  background: 'var(--c-surface, #fff)',
                                  border: '1px solid var(--c-border, #e5e7eb)',
                                  borderRadius: 12,
                                  padding: 12,
                                  minWidth: 240,
                                  maxWidth: 340,
                                  color: 'var(--c-text, #111)',
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: orgLine ? 4 : 10 }}>
                                    <div style={{
                                      width: 38, height: 38, borderRadius: '50%',
                                      background: '#25d366', color: '#fff',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontWeight: 700, fontSize: 16, flexShrink: 0,
                                    }}>
                                      <User size={18} />
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div style={{ fontWeight: 600, fontSize: 14, wordBreak: 'break-word' }}>{displayName}</div>
                                      {orgLine && (
                                        <div style={{ fontSize: 11, color: 'var(--c-text-muted, #6b7280)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                          <Briefcase size={11} /> {orgLine}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {phones.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                                      {phones.map((p, pi) => {
                                        const raw = p.phone || p.wa_id || '';
                                        const telHref = raw.replace(/\s+/g, '');
                                        const linkKey = `${msg.id}:${raw}`;
                                        const linking = !!linkingSharedContact[linkKey];
                                        return (
                                          <div key={pi} style={{
                                            background: 'var(--c-surface-2, #f3f4f6)',
                                            borderRadius: 8,
                                            padding: '6px 8px',
                                            display: 'flex', flexDirection: 'column', gap: 6,
                                          }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                                              <Phone size={13} style={{ flexShrink: 0 }} />
                                              <a href={`tel:${telHref}`} style={{ color: 'inherit', textDecoration: 'none', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-all' }}>
                                                {raw}
                                              </a>
                                              {p.type && <span style={{ fontSize: 10, color: 'var(--c-text-muted, #6b7280)', textTransform: 'uppercase' }}>{p.type}</span>}
                                            </div>
                                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-success"
                                                style={{ padding: '3px 8px', fontSize: 11 }}
                                                onClick={() => openComposeWithPhone(raw, displayName)}
                                                title={t('whatsapp.vcard.writeWa', 'Escribir por WhatsApp')}
                                              >
                                                <MessageCircle size={12} /> {t('whatsapp.vcard.writeWaShort', 'WhatsApp')}
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-ghost"
                                                style={{ padding: '3px 8px', fontSize: 11 }}
                                                disabled={linking}
                                                onClick={() => handleLinkSharedContact(card, p, msg.id)}
                                                title={t('whatsapp.vcard.linkToTicket', 'Vincular al ticket y agregar a contactos')}
                                              >
                                                {linking ? <Loader2 size={12} className="spin" /> : <Link2 size={12} />}
                                                {' '}{t('whatsapp.vcard.linkShort', 'Vincular')}
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {emails.length > 0 && (
                                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                      {emails.map((e, ei) => (
                                        <a key={ei} href={`mailto:${e.email}`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--c-brand, #25d366)', textDecoration: 'none', wordBreak: 'break-all' }}>
                                          <Mail size={12} /> {e.email}
                                          {e.type && <span style={{ fontSize: 10, color: 'var(--c-text-muted, #6b7280)', textTransform: 'uppercase' }}>{e.type}</span>}
                                        </a>
                                      ))}
                                    </div>
                                  )}

                                  {urls.length > 0 && (
                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                      {urls.map((u, ui) => (
                                        <a key={ui} href={u.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--c-brand, #25d366)', textDecoration: 'none', wordBreak: 'break-all' }}>
                                          <Globe size={12} /> {u.url}
                                        </a>
                                      ))}
                                    </div>
                                  )}

                                  {addresses.length > 0 && (
                                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                      {addresses.map((a, ai) => {
                                        const line = [a.street, a.city, a.state, a.zip, a.country].filter(Boolean).join(', ');
                                        if (!line) return null;
                                        return (
                                          <div key={ai} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 12, color: 'var(--c-text-muted, #6b7280)' }}>
                                            <MapPin size={12} style={{ marginTop: 2, flexShrink: 0 }} /> {line}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* Un mail enviado desde el mismo ticket (sendClientEmailFromTicket) NO salió por
                            WhatsApp: se muestra como aviso compacto con el texto limpio. Antes su HTML
                            se escapaba y aparecía como una burbuja verde con <p> y <br> crudos, como si
                            le hubiéramos mandado eso al cliente por WhatsApp (#15091, 19-oct-2026). */}
                        {/* Sólo cuenta como mail si de verdad salió (tiene email_message_id): la respuesta
                            por WhatsApp se inserta con channel='email' (default de la columna) y recién pasa
                            a 'whatsapp' después del envío a Meta; en ese medio segundo el refresh la pintaba
                            como "📧 Mail enviado" y después desaparecía (27-oct-2026). */}
                        {isSentEmail(msg) && (
                          <div className="wa-bubble-msg-text" style={{ fontSize: 12.5 }}>
                            {!msg.is_private && (
                              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                                📧 {msg.email_to ? t('whatsapp.bubble.emailSentTo', { to: msg.email_to }) : t('whatsapp.bubble.emailSent')}
                              </div>
                            )}
                            <div style={{ opacity: 0.85 }}>{toSnippet(msg.body_text || msg.body_html, 220)}</div>
                          </div>
                        )}
                        {!isSentEmail(msg) && msg.body_text && msg.rich_metadata?.kind !== 'contacts' && (
                          <div className="wa-bubble-msg-text" dangerouslySetInnerHTML={{ __html: msg.body_text
                            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                            .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline">$1</a>')
                            .replace(/(\+?\d[\d\s\-().]{7,}\d)/g, '<a href="tel:$1" style="color:inherit;text-decoration:underline" title="Llamar / copiar">$1</a>')
                            .replace(/\n/g, '<br>')
                          }} />
                        )}
                        <div className="wa-bubble-msg-meta">
                          <span>{new Date(msg.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</span>
                          {isAgent && <DeliveryTick status={msg.wa_status} reason={waFailReason(msg.wa_error, t)} />}
                        </div>
                        {isAgent && msg.wa_status === 'failed' && (
                          <div style={{ marginTop: 2, fontSize: 11, color: '#ef4444', lineHeight: 1.4 }}>
                            {waFailReason(msg.wa_error, t)}
                          </div>
                        )}
                        {/* Action toolbar: Reply + React (visible for any WA message that's not private).
                            Mirrors WhatsApp Web's hover-toolbar behavior. En los chats embebidos
                            (Oxígeno, medisoft) no hay wa_message_id, pero reaccionar también es del
                            hilo: sin esto el cliente podía reaccionar y el técnico no. Responder
                            citando sí es de WhatsApp y queda sólo ahí. */}
                        {!msg.is_private && (msg.wa_message_id || selectedConv?.external_source) && (
                          <div style={{
                            position: 'absolute', top: 4, right: 4,
                            display: 'flex', gap: 3, alignItems: 'center'
                          }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReactionPickerFor(reactionPickerFor === msg.id ? null : msg.id);
                              }}
                              title={t('whatsapp.bubble.react', 'Reaccionar')}
                              aria-label={t('whatsapp.bubble.react', 'Reaccionar')}
                              style={{
                                width: 22, height: 22, borderRadius: '50%',
                                background: 'rgba(0,0,0,0.35)', color: '#fff', border: 'none',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, opacity: 0.55, transition: 'opacity 0.15s'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.55'; }}
                            >
                              😊
                            </button>
                            {msg.wa_message_id && <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReplyingTo(msg);
                                setTimeout(() => textareaRef.current?.focus(), 50);
                              }}
                              title={t('whatsapp.bubble.reply', 'Responder')}
                              aria-label={t('whatsapp.bubble.reply', 'Responder')}
                              style={{
                                width: 22, height: 22, borderRadius: '50%',
                                background: 'rgba(0,0,0,0.35)', color: '#fff', border: 'none',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                opacity: 0.55, transition: 'opacity 0.15s'
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.55'; }}
                            >
                              <CornerUpLeft size={12} />
                            </button>}
                          </div>
                        )}
                        {/* Quick-reaction picker — 6 emojis. Click to react, click again same emoji to remove. */}
                        {reactionPickerFor === msg.id && (
                          <div style={{
                            position: 'absolute', top: -34, right: 4, zIndex: 20,
                            background: 'var(--c-surface)', border: '1px solid var(--c-border)',
                            borderRadius: 18, padding: '4px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                            display: 'flex', gap: 2
                          }}>
                            {['👍', '❤️', '😂', '😮', '😢', '🙏'].map(e => (
                              <button
                                key={e}
                                type="button"
                                onClick={(ev) => { ev.stopPropagation(); handleReact(msg, e); }}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  fontSize: 18, padding: '2px 4px', borderRadius: 12,
                                  transition: 'transform 0.1s'
                                }}
                                onMouseEnter={(ev) => { ev.currentTarget.style.transform = 'scale(1.3)'; }}
                                onMouseLeave={(ev) => { ev.currentTarget.style.transform = 'scale(1)'; }}
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                        )}
                        {/* Reaction badges — group by emoji, show count. Click own reaction to remove. */}
                        {msg.wa_reactions && Object.keys(msg.wa_reactions).length > 0 && (() => {
                          const grouped = {};
                          Object.entries(msg.wa_reactions).forEach(([key, r]) => {
                            if (!r?.emoji) return;
                            if (!grouped[r.emoji]) grouped[r.emoji] = { count: 0, mine: false, names: [] };
                            grouped[r.emoji].count++;
                            grouped[r.emoji].names.push(r.by || key);
                            if (key === user?.id) grouped[r.emoji].mine = true;
                          });
                          return (
                            <div style={{
                              position: 'absolute', bottom: -10, right: isAgent ? 8 : 'auto', left: isAgent ? 'auto' : 8,
                              display: 'flex', gap: 2
                            }}>
                              {Object.entries(grouped).map(([emoji, info]) => (
                                <button
                                  key={emoji}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (info.mine) handleReact(msg, emoji);
                                  }}
                                  title={info.names.join(', ')}
                                  style={{
                                    background: info.mine ? 'rgba(37,211,102,0.2)' : 'var(--c-surface)',
                                    border: `1px solid ${info.mine ? '#25d366' : 'var(--c-border)'}`,
                                    borderRadius: 12, padding: '1px 6px',
                                    fontSize: 12, cursor: info.mine ? 'pointer' : 'default',
                                    display: 'inline-flex', alignItems: 'center', gap: 3
                                  }}
                                >
                                  <span>{emoji}</span>
                                  {info.count > 1 && <span style={{ fontSize: 10, fontWeight: 600 }}>{info.count}</span>}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* @mention autocomplete */}
              {mentionQuery && mentionMatches.length > 0 && (
                <div style={{
                  background: 'var(--c-surface, #fff)', borderTop: '1px solid var(--c-border, #e5e7eb)',
                  maxHeight: 160, overflowY: 'auto', padding: '4px 0'
                }}>
                  {mentionMatches.map((a, i) => (
                    <div
                      key={a.id}
                      onClick={() => insertMention(a)}
                      style={{
                        padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                        background: i === mentionIndex ? 'var(--c-surface-2, #f3f4f6)' : 'transparent',
                        fontSize: 13
                      }}
                    >
                      {a.avatar_url
                        ? <img src={a.avatar_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                        : <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#3b82f6', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                            {(a.first_name || '?')[0]}
                          </div>
                      }
                      <span style={{ fontWeight: 600 }}>{a.first_name} {a.last_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Input */}
              {(() => {
                // OJO: este bloque no es el aviso de sesión, es TODO el campo de escritura.
                // Devolver null acá deja al agente sin manera de responder — pasó con los
                // chats de las plataformas integradas, que no tienen ventana de 24 h.
                // La rama de plantillas es lo ÚNICO específico de WhatsApp: si no es
                // WhatsApp se saltea y se cae al composer normal de abajo.
                const s = esWhatsApp(selectedConv) ? formatSessionCountdown(selectedConv.wa_session_expires_at) : null;
                if (s && (s.expired || s.waiting)) {
                  return (
                    <div className="wa-bubble-input" style={{ flexDirection: 'column', gap: 8, padding: '12px 16px' }}>
                      <div style={{ fontSize: 12, color: '#f59e0b', textAlign: 'center' }}>
                        {s.waiting
                          ? t('whatsapp.resend.waitingReply', 'El contacto aún no respondió — solo se pueden enviar plantillas')
                          : t('whatsapp.resend.sessionExpired', 'Sesión expirada — enviá una plantilla para reabrir la conversación')}
                      </div>
                      <select
                        value={selectedConv._resendTemplate || ''}
                        onChange={e => {
                          const tplName = e.target.value;
                          selectedConv._resendTemplate = tplName;
                          setMsgText(tplName ? ' ' : '');
                          // Extract param placeholders from template body and pre-fill
                          if (tplName) {
                            const tpl = usableTemplates.find(t => t.template_name === tplName);
                            const body = tpl?.body_text || '';
                            const matches = [...body.matchAll(/\{\{(\w+)\}\}/g)];
                            const seen = new Set();
                            const params = [];
                            matches.forEach(m => {
                              if (!seen.has(m[1])) {
                                seen.add(m[1]);
                                // Auto-fill: first param = contact name, second = ticket subject
                                const idx = params.length;
                                let defaultVal = '';
                                if (idx === 0) defaultVal = selectedConv.contact_name || '';
                                else if (idx === 1) defaultVal = (selectedConv.subject || '').replace(/^WhatsApp:\s*/i, '');
                                params.push(defaultVal);
                              }
                            });
                            selectedConv._resendParams = params;
                          } else {
                            selectedConv._resendParams = [];
                          }
                        }}
                        style={{ width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 8, border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text)' }}
                      >
                        <option value="">{t('whatsapp.compose.selectTemplate', 'Seleccionar plantilla...')}</option>
                        {usableTemplates.map(t => <option key={t.id} value={t.template_name}>{t.template_name.replace(/_/g, ' ')}</option>)}
                      </select>
                      {selectedConv._resendTemplate && (() => {
                        const tpl = usableTemplates.find(t => t.template_name === selectedConv._resendTemplate);
                        const body = tpl?.body_text || '';
                        const matches = [...body.matchAll(/\{\{(\w+)\}\}/g)];
                        const seen = new Set();
                        const paramNames = [];
                        matches.forEach(m => { if (!seen.has(m[1])) { seen.add(m[1]); paramNames.push(m[1]); } });
                        const params = selectedConv._resendParams || [];
                        // Live preview: substitute each placeholder with the typed param so the
                        // agent sees the exact text the client will receive before sending.
                        const previewText = body.replace(/\{\{(\w+)\}\}/g, (m0, name) => {
                          const i = paramNames.indexOf(name);
                          return (i >= 0 && params[i]) ? params[i] : m0;
                        });
                        return (
                          <>
                            {tpl?.category && (
                              <span style={{
                                alignSelf: 'flex-start', padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                                fontSize: 10, letterSpacing: 0.3, textTransform: 'uppercase',
                                background: 'rgba(37,211,102,0.15)', color: '#25d366',
                                border: '1px solid rgba(37,211,102,0.35)',
                              }}>
                                {tpl.category}
                              </span>
                            )}
                            <div style={{
                              padding: '8px 10px', background: 'rgba(255,255,255,0.05)',
                              borderRadius: 8, fontSize: 12, lineHeight: 1.5,
                              border: '1px solid rgba(134,150,160,0.2)', whiteSpace: 'pre-wrap',
                            }}>
                              {previewText}
                            </div>
                            {paramNames.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {paramNames.map((name, i) => (
                                  <input
                                    key={name}
                                    placeholder={
                                      ['nombre', 'name', '1'].includes(name.toLowerCase())
                                        ? t('whatsapp.compose.namePlaceholder', 'Nombre del contacto')
                                        : ['motivo', 'reason', 'mensaje', '2'].includes(name.toLowerCase())
                                          ? t('whatsapp.compose.reasonPlaceholder', 'Ej: seguimiento de ticket, consulta técnica...')
                                          : `{{${name}}}`
                                    }
                                    type="text"
                                    value={params[i] || ''}
                                    onChange={e => {
                                      const updated = [...(selectedConv._resendParams || [])];
                                      updated[i] = e.target.value;
                                      selectedConv._resendParams = updated;
                                      setMsgText(' ' + Date.now()); // force re-render
                                    }}
                                    style={{ width: '100%', padding: '6px 10px', fontSize: 12, borderRadius: 6, border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text)' }}
                                  />
                                ))}
                              </div>
                            )}
                            <button
                              className="wa-bubble-send-btn"
                              style={{ width: '100%', padding: '8px', borderRadius: 8, fontSize: 13 }}
                              disabled={sending}
                              onClick={async () => {
                                setSending(true);
                                try {
                                  const { data } = await api.post(`/tickets/${selectedConv.id}/messages`, { body: '', templateName: selectedConv._resendTemplate, templateParams: selectedConv._resendParams || [] });
                                  selectedConv._resendTemplate = '';
                                  selectedConv._resendParams = [];
                                  // Closed WA tickets get redirected to a new linked child by the
                                  // backend so the template + the customer's reply land on the
                                  // same (new) ticket — switch to it.
                                  if (data?.redirectedToTicket?.id) {
                                    toast.success(t('whatsapp.compose.redirectedToNew', { n: data.redirectedToTicket.ticket_number, defaultValue: `Ticket cerrado — se creó #${data.redirectedToTicket.ticket_number} para esta conversación` }));
                                    await fetchConversations();
                                    selectConversation({ id: data.redirectedToTicket.id });
                                  } else {
                                    toast.success(t('whatsapp.compose.templateSent', 'Template enviado'));
                                    await loadMessages(selectedConv.id);
                                    fetchConversations();
                                  }
                                } catch (err) { toast.error(err.response?.data?.error || t('common.error', 'Error')); }
                                setSending(false);
                              }}
                            >
                              <Send size={14} /> Enviar template
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  );
                }
                return null;
              })()}
              {/* ESTE bloque envuelve el composer ENTERO — el campo de texto, los adjuntos,
                  el micrófono y el botón de enviar. Devolver null acá deja al agente sin
                  ninguna manera de responder, que es exactamente lo que pasó con los chats
                  de las plataformas integradas. La ventana de 24 h es de WhatsApp: si la
                  conversación no es de WhatsApp NO hay sesión que chequear y el composer
                  tiene que renderizarse siempre. */}
              {(() => { const s = esWhatsApp(selectedConv) ? formatSessionCountdown(selectedConv.wa_session_expires_at) : null; return (s && (s.expired || s.waiting)) ? null : (
              <>
              {selectedConv?.id && (
                <div style={{ padding: '6px 10px 0', flexShrink: 0, background: 'var(--c-surface, #fff)', borderTop: '1px solid var(--c-border, #e5e7eb)' }}>
                  <AiSuggestionCard
                    ticketId={selectedConv.id}
                    compact
                    onAccept={(text) => {
                      setMsgText(text);
                      setTimeout(() => textareaRef.current?.focus(), 50);
                    }}
                  />
                </div>
              )}
              {/* Reply preview bar — shows the message being quoted with a clear button */}
              {replyingTo && (
                <div style={{
                  background: 'rgba(37,211,102,0.08)', borderLeft: '3px solid #25d366',
                  padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10,
                  borderTop: '1px solid var(--c-border)'
                }}>
                  <CornerUpLeft size={14} style={{ color: '#25d366', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#25d366', marginBottom: 2 }}>
                      {replyingTo.author_type === 'agent'
                        ? `${replyingTo.first_name || ''} ${replyingTo.last_name || ''}`.trim() || t('tickets.systemBot', 'Agente')
                        : replyingTo.contact_name || t('tickets.replyToContact', 'Cliente')}
                    </div>
                    <div style={{
                      color: 'var(--c-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {replyingTo.body_text
                        || (replyingTo.attachments?.[0]?.mime_type?.startsWith('image/') ? '📷 Foto' : null)
                        || (replyingTo.attachments?.[0]?.mime_type?.startsWith('audio/') ? '🎤 Audio' : null)
                        || (replyingTo.attachments?.[0]?.mime_type?.startsWith('video/') ? '🎬 Video' : null)
                        || (replyingTo.attachments?.[0] ? `📎 ${replyingTo.attachments[0].original_name || 'archivo'}` : '')}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setReplyingTo(null)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--c-text-muted)', padding: 4, display: 'flex', alignItems: 'center'
                    }}
                    title={t('common.cancel', 'Cancelar')}
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              {/* Aviso de pedido de llamada / pantalla y "está escribiendo": van ARRIBA de
                  la fila del composer, a todo el ancho. Antes estaban adentro de esa fila
                  flex y le peleaban el ancho al textarea: el texto se partía palabra por
                  palabra y el cuadro de escribir quedaba aplastado, sin poder responder. */}
              {pedidoDeLlamada && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', flexWrap: 'wrap',
                  background: 'rgba(35,181,166,0.12)', borderTop: '1px solid rgba(35,181,166,0.35)',
                }}>
                  <span style={{ flex: '1 1 160px', fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>
                    {pedidoDeLlamada === 'screen'
                      ? t('whatsapp.callRequest.screen', 'El cliente ofrece compartir su pantalla')
                      : pedidoDeLlamada === 'video'
                        ? t('whatsapp.callRequest.video', 'El cliente pide una videollamada')
                        : t('whatsapp.callRequest.audio', 'El cliente pide una llamada')}
                  </span>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {pedidoDeLlamada === 'screen' ? (
                      <button className="btn btn-primary btn-sm" disabled={abriendoAsistencia} onClick={abrirAsistencia}>
                        {t('whatsapp.callRequest.acceptScreen', 'Aceptar y ver pantalla')}
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => { setVoiceCallMode(pedidoDeLlamada); setVoiceCallOpen(true); }}
                      >
                        {t('whatsapp.callRequest.answer', 'Llamar ahora')}
                      </button>
                    )}
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setPedidosDescartados(prev => new Set(prev).add(pedido.id))}
                      title={t('whatsapp.callRequest.dismissHint', 'Ocultar este aviso')}
                    >
                      {t('whatsapp.callRequest.dismiss', 'Ahora no')}
                    </button>
                  </div>
                </div>
              )}
              {avisoEscribiendo}
              <div className="wa-bubble-input wa-bubble-input--with-ai">
                {recording ? (
                  /* Recording mode */
                  <>
                    <button className="wa-bubble-attach-btn" onClick={cancelRecording} title="Cancelar">
                      <X size={18} style={{ color: '#ef4444' }} />
                    </button>
                    <div className="wa-bubble-recording-indicator">
                      <span className="wa-bubble-rec-dot" />
                      <span>{formatRecTime(recordingTime)}</span>
                    </div>
                    <button className="wa-bubble-send-btn" onClick={stopRecording} title="Enviar audio">
                      <Send size={18} />
                    </button>
                  </>
                ) : (
                  /* Normal mode */
                  <>
                    <button
                      className="wa-bubble-attach-btn"
                      onClick={() => fileInputRef.current?.click()}
                      title="Adjuntar"
                    >
                      <Paperclip size={18} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleAttachment}
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip"
                    />
                    <EmojiPickerButton
                      size={18}
                      onEmojiSelect={(emoji) => { setMsgText(prev => prev + emoji); textareaRef.current?.focus(); }}
                      buttonClass="wa-bubble-attach-btn"
                    />
                    <div style={{ position: 'relative' }} ref={quickActionsRef}>
                      <button
                        className="wa-bubble-attach-btn"
                        onClick={() => setShowQuickActions(!showQuickActions)}
                        title={t('whatsapp.bubble.quickActions', 'Acciones rápidas')}
                        style={showQuickActions ? { color: 'var(--c-primary)' } : undefined}
                      >
                        <Zap size={18} />
                      </button>
                      {showQuickActions && (
                        <div
                          style={{
                            position: 'absolute', bottom: '100%', left: 0, marginBottom: 6,
                            background: 'var(--c-surface)', border: '1px solid var(--c-border)',
                            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            zIndex: 50, width: 220, overflow: 'hidden',
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--c-border)', fontSize: 11, fontWeight: 600, color: 'var(--c-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            {t('whatsapp.bubble.quickActions', 'Acciones rápidas')}
                          </div>
                          {[
                            {
                              icon: <Cpu size={15} />,
                              label: t('tickets.sendDiag'),
                              action: async () => {
                                setShowQuickActions(false);
                                if (!window.confirm(t('tickets.confirmSendDiag'))) return;
                                setSending(true);
                                try {
                                  const { data: session } = await api.post('/diagnostics/sessions', {
                                    label: `${selectedConv.contact_name || 'Cliente'} - #${selectedConv.ticket_number}`,
                                    ticket_id: selectedConv.id,
                                    contact_id: selectedConv.contact_id || null,
                                    company_id: selectedConv.company_id || null,
                                  });
                                  const fd = new FormData();
                                  fd.append('body', session.whatsappText);
                                  await api.post(`/tickets/${selectedConv.id}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                  toast(t2 => (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                      {t('tickets.diagSent')}
                                      <button onClick={() => { toast.dismiss(t2.id); navigate('/diagnostics'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary)', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {t('common.view')}
                                      </button>
                                    </span>
                                  ), { icon: '✅', duration: 10000 });
                                  await loadMessages(selectedConv.id);
                                } catch {
                                  toast.error(t('common.sendFailed'));
                                } finally {
                                  setSending(false);
                                }
                              },
                            },
                            {
                              icon: <Video size={15} />,
                              label: t('tickets.sendLiveAssist'),
                              action: async () => {
                                setShowQuickActions(false);
                                if (!window.confirm(t('tickets.confirmSendLiveAssist'))) return;
                                setSending(true);
                                try {
                                  const { data: session } = await api.post('/remote-assist/sessions', {
                                    ticket_id: selectedConv.id,
                                    company_id: selectedConv.company_id || null,
                                    contact_name: selectedConv.contact_name || '',
                                    contact_email: '',
                                  });
                                  const url = `${window.location.origin}/assist/${session.token}`;
                                  const msg = t('tickets.liveAssistMsg', { url });
                                  const fd = new FormData();
                                  fd.append('body', msg);
                                  await api.post(`/tickets/${selectedConv.id}/messages`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                  toast(t2 => (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                                      {t('tickets.liveAssistSent')}
                                      <button onClick={() => { toast.dismiss(t2.id); window.open(`/remote-assist/${session.id}`, '_blank'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-primary)', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>
                                        {t('tickets.joinSession')}
                                      </button>
                                    </span>
                                  ), { icon: '✅', duration: 10000 });
                                  await loadMessages(selectedConv.id);
                                } catch {
                                  toast.error(t('common.sendFailed'));
                                } finally {
                                  setSending(false);
                                }
                              },
                            },
                            {
                              icon: <ShoppingCart size={15} />,
                              label: t('whatsapp.bubble.quickQuote', 'Cotización rápida'),
                              action: () => { setShowQuickActions(false); setShowQuickQuote(true); },
                            },
                            ...(hasModule('rag_search') ? [{
                              icon: <Sparkles size={15} style={{ color: '#8b5cf6' }} />,
                              label: t('aiAssist.title'),
                              action: () => { setShowQuickActions(false); setShowAiAssist(true); },
                            }] : []),
                            {
                              icon: <BookOpen size={15} />,
                              label: t('tickets.shareKBArticle'),
                              action: () => { setShowQuickActions(false); setShowKBShare(true); },
                            },
                            ...(hasModule('vault') ? [{
                              icon: <KeyRound size={15} style={{ color: '#f59e0b' }} />,
                              label: t('vault.shareModal.title', 'Compartir acceso'),
                              action: () => { setShowQuickActions(false); setShowShareCred(true); },
                            }] : []),
                            {
                              icon: <CalendarDays size={15} />,
                              label: t('tickets.scheduleVisit'),
                              action: () => { setShowQuickActions(false); setShowScheduleVisit(true); },
                            },
                          ].map((item, i) => (
                            <button
                              key={i}
                              disabled={sending}
                              onClick={item.action}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                                padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: 13, color: 'var(--c-text)', transition: 'background 0.15s',
                                opacity: sending ? 0.5 : 1,
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'var(--c-bg)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                            >
                              <span style={{ color: 'var(--c-text-muted)', flexShrink: 0 }}>{item.icon}</span>
                              {item.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {showQuickQuote && (
                      <QuickQuoteModal
                        channel="whatsapp"
                        ticketId={selectedConv?.id}
                        onClose={() => setShowQuickQuote(false)}
                        onInsert={async (text, cartItems, services) => {
                          setMsgText(prev => prev ? prev + '\n\n' + text : text);
                          setShowQuickQuote(false);
                          setTimeout(() => textareaRef.current?.focus(), 50);
                          // Auto-create budget to track in ticket quote box
                          if ((cartItems?.length || services?.length) && selectedConv?.id) {
                            try {
                              const budgetRes = await api.post('/budgets', {
                                ticket_id: selectedConv.id,
                                company_id: selectedConv.company_id || null,
                                client_name: selectedConv.contact_name || '',
                                client_email: '',
                                project_name: `${t('tickets.quote', 'Cotización')} - #${selectedConv.ticket_number || ''}`,
                                currency: 'USD',
                              });
                              const budgetId = budgetRes.data.id;
                              if (cartItems?.length > 0) {
                                const eqSec = await api.post(`/budgets/${budgetId}/sections`, { title: t('tickets.hardwareSection', 'Equipamiento'), section_type: 'items', item_category: 'equipment', sort_order: 0 });
                                for (const item of cartItems) {
                                  await api.post(`/budgets/${budgetId}/sections/${eqSec.data.id}/items`, {
                                    description: item.title, quantity: item.qty || 1, unit_price_usd: item.price,
                                    item_type: 'equipment', polytech_source_id: item.source_id, polytech_cost_usd: item.original_price || null,
                                    provider_source: item._supplier || null,
                                  });
                                }
                              }
                              if (services?.length > 0) {
                                const svcSec = await api.post(`/budgets/${budgetId}/sections`, { title: t('tickets.serviceSection', 'Servicios'), section_type: 'items', item_category: 'labor', sort_order: 1 });
                                for (const svc of services) {
                                  await api.post(`/budgets/${budgetId}/sections/${svcSec.data.id}/items`, {
                                    description: svc.title, quantity: svc.qty || 1, unit_price_usd: svc.price, item_type: 'labor',
                                    provider_source: 'service',
                                  });
                                }
                              }
                              toast.success(t('tickets.budgetLinked', 'Presupuesto vinculado al ticket'));
                            } catch (err) { console.error('[QUOTE] Budget auto-create failed:', err); toast.error(t('tickets.budgetLinkError', 'Error al crear presupuesto')); }
                          }
                        }}
                      />
                    )}
                    {showAiAssist && (() => {
                      const lastInbound = [...messages].reverse().find(m => m.author_type === 'contact');
                      const seedRaw = lastInbound?.body || selectedConv?.subject || '';
                      const seed = String(seedRaw).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                      return (
                        <AiAssistPanel
                          open={showAiAssist}
                          onClose={() => setShowAiAssist(false)}
                          contextText={seed}
                          clientId={selectedConv?.company_id || null}
                          onInsert={(text) => {
                            setMsgText(prev => prev ? prev + '\n\n' + text : text);
                            setTimeout(() => textareaRef.current?.focus(), 50);
                          }}
                        />
                      );
                    })()}
                    {showKBShare && (
                      <KBArticlePicker
                        onSelect={async (article) => {
                          // Link article to ticket
                          if (selectedConv?.id) {
                            try { await api.post('/kb/ticket-links', { article_id: article.id, ticket_id: selectedConv.id }); } catch {}
                          }
                          const plainBody = (article.body || '').replace(/<[^>]+>/g, '').trim();
                          const text = `📖 *${article.title}*\n\n${plainBody}`;
                          setMsgText(prev => prev ? prev + '\n\n' + text : text);
                          setShowKBShare(false);
                          setTimeout(() => textareaRef.current?.focus(), 50);
                        }}
                        onClose={() => setShowKBShare(false)}
                      />
                    )}
                    {showShareCred && (
                      <ShareCredentialModal
                        companyId={selectedConv?.company_id}
                        companyName={selectedConv?.company_name}
                        contactId={selectedConv?.contact_id}
                        defaultQuery={selectedConv?.company_name}
                        onInsert={(text) => {
                          setMsgText(prev => prev ? prev + '\n\n' + text : text);
                          setTimeout(() => textareaRef.current?.focus(), 50);
                        }}
                        onClose={() => setShowShareCred(false)}
                      />
                    )}
                    {showScheduleVisit && selectedConv && (
                      <React.Suspense fallback={null}>
                        <ScheduleVisitModal
                          onClose={() => setShowScheduleVisit(false)}
                          onSaved={() => { setShowScheduleVisit(false); toast.success(t('tickets.visitScheduled')); }}
                          ticket={{ id: selectedConv.id, subject: `${selectedConv.contact_name || ''} - #${selectedConv.ticket_number}`, company_id: selectedConv.company_id, company_id_resolved: selectedConv.company_id, contact_name: selectedConv.contact_name }}
                          isManagerRole={true}
                          currentUserId={user?.id}
                        />
                      </React.Suspense>
                    )}
                    <textarea
                      ref={textareaRef}
                      className="wa-bubble-textarea"
                      placeholder={t('whatsapp.bubble.typeMessage', 'Escribe un mensaje...')}
                      value={msgText}
                      onChange={handleTextareaInput}
                      onKeyDown={handleKeyDown}
                      rows={1}
                      disabled={sending}
                    />
                    {msgText.trim() ? (
                      <button
                        className="wa-bubble-send-btn"
                        onClick={handleSend}
                        disabled={sending}
                      >
                        <Send size={18} />
                      </button>
                    ) : (
                      <button
                        className="wa-bubble-mic-btn"
                        onClick={startRecording}
                        disabled={sending}
                        title="Grabar audio"
                      >
                        <Mic size={18} />
                      </button>
                    )}
                  </>
                )}
              </div>
              </>
              ); })()}
            </>
          )}
        </div>
      )}

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          startIndex={lightboxImage.startIndex}
          onClose={() => setLightboxImage(null)}
          onSendEdited={selectedConv ? async (blob, fileName) => {
            const fd = new FormData();
            fd.append('body', '');
            fd.append('attachments', blob, fileName);
            await api.post(`/tickets/${selectedConv.id}/messages`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            await loadMessages(selectedConv.id);
            fetchConversations();
          } : undefined}
        />
      )}
    </>,
    document.body
  );
}
